import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Dropdown,
  Input,
  Modal,
  Message,
  Popover,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "@arco-design/web-react";
import type { TableColumnProps } from "@arco-design/web-react";
import {
  ArrowDown,
  ArrowUp,
  ClipboardList,
  Clock3,
  Columns3,
  Gift,
  Plus,
  RotateCcw,
  Search,
  Star,
  Trash2,
  UserRoundCheck,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { flushSync } from "react-dom";
import { canAccessCrmOperation, canUsePermission, type PermissionUser } from "../../../lib/auth";
import { getProvinceOptions } from "../../../shared/region";
import { CrmListFilterMoreButton } from "../../../shared/ui/CrmListFilterMoreButton";
import { SearchHistoryInput } from "../../../shared/ui/SearchHistoryInput";
import {
  rememberSearchHistory,
  SEARCH_HISTORY_KEYS,
} from "../../../lib/searchHistoryStorage";
import { useCurrentAuthUser } from "../../auth";
import { QuickOpportunityDrawer } from "../../crm_opportunity_workbench";

import {
  crmContactWorkbenchColumnOptions,
  crmContactWorkbenchDefaultVisibleColumns,
  CONTACT_STAR_OPTION_DESCRIPTIONS,
  formatContactStarOptionLabel,
} from "../config";
import {
  CONTACT_WORKBENCH_MOBILE_PAGE_SIZE,
  useCrmContactWorkbenchRuntime,
} from "../runtime/useCrmContactWorkbenchRuntime";
import { ContactGiftModal } from "./ContactGiftModal";
import {
  MobileContactFilterBar,
  MobileContactList,
  MobileContactSortBar,
  useIsMobileViewport,
} from "./CrmContactWorkbenchMobile";
import {
  ContactEditor,
  createContactDraft,
  createEmptyContactDraft,
  getMobileValidationError,
  mapContactDraftToPayload,
  type ContactEditorDraft,
} from "./ContactEditorPanel";
import { searchContactWorkbenchOrganizations } from "../repo/crmContactWorkbenchRepo";
import { WorkbenchColumnSettings, loadWorkbenchVisibleColumnsPreference, normalizeWorkbenchVisibleColumns, saveWorkbenchVisibleColumnsPreference } from "../../../shared/ui/WorkbenchColumnSettings";
import {
  stabilizeHorizontalOverflow,
  stabilizeLayoutMetric,
} from "../../../shared/ui/stabilizeLayoutMetric";
import {
  ContactRecordCreator,
  ContactRecordList,
  createDefaultContactRecordDraft,
  mapContactRecordDraftToPayload,
  resolveContactRecordLabelColor,
  splitContactRecordLabels,
  type ContactRecordDraft,
} from "./ContactRecordPanels";
import {
  buildSchoolBatchAssignmentRegionOptions,
  filterSchoolsByBatchAssignmentRegions,
  formatContactArea,
  formatContactOwners,
  formatContactWorkbenchError,
  filterOwnerOptionsByKeyword,
  formatContactStar,
  formatContactWorkbenchHour,
  buildContactFilterSchoolOptionsFromSelection,
  mergeContactFilterSchoolOptions,
  matchOrganizationsByExactNames,
  pickBestOrganizationMatch,
  resolveContactFilterOrgNames,
  splitPastedSchoolNames,
} from "../service/crmContactWorkbenchService";
import type {
  ContactWorkbenchColumnKey,
  ContactWorkbenchItem,
  ContactWorkbenchNlQueryParseResult,
  ContactWorkbenchOrganizationOption,
  ContactWorkbenchOwnerKind,
  ContactWorkbenchOwnerOption,
  ContactWorkbenchOwnerOptions,
  ContactWorkbenchRecordItem,
  ContactWorkbenchSelectionMode,
  ContactWorkbenchSchoolBatchAssignmentMode,
  ContactWorkbenchStaffOption,
  ContactWorkbenchTaskPoolType,
  ContactWorkbenchTemporaryTaskAssignment,
} from "../types";
import "./CrmContactWorkbenchPage.css";
import "../../../shared/ui/crmListPageLayout.css";
import "../../../shared/ui/adminEditorForm.css";

type SortDirection = "asc" | "desc";

type SharedOwnerEditContext = {
  contactId: number;
  contactName: string;
  currentUserIds: number[];
  currentUserNames: string[];
};

type SharedOwnerDeleteContext = {
  item: ContactWorkbenchItem;
  userId: number;
  userName: string;
};

type TemporaryTaskEditContext = {
  contactId: number;
  contactName: string;
  assignment: ContactWorkbenchTemporaryTaskAssignment;
};

const CONTACT_WORKBENCH_VISIBLE_COLUMNS_STORAGE_KEY =
  "flower.crm_contact_workbench.visible_columns.v1";

/** CRM 联系人工作台主页面。 */
export function CrmContactWorkbenchFeaturePage() {
  const currentUserQuery = useCurrentAuthUser();
  const runtime = useCrmContactWorkbenchRuntime(currentUserQuery.data?.id);
  const permissionUser = currentUserQuery.data;
  const isMobileViewport = useIsMobileViewport();
  const tableShellRef = useRef<HTMLDivElement | null>(null);
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const syncingScrollRef = useRef(false);
  const schoolBatchAssignmentPreviewRequestRef = useRef(0);
  const schoolBatchAssignmentScopeRequestRef = useRef(0);
  const schoolBatchAssignmentSchoolIdsRef = useRef<number[]>([]);
  const [topScrollWidth, setTopScrollWidth] = useState(0);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const hasHorizontalOverflowRef = useRef(false);
  const [tableScrollY, setTableScrollY] = useState(520);
  const [advancedFiltersVisible, setAdvancedFiltersVisible] = useState(false);
  const [filterSchoolOptions, setFilterSchoolOptions] = useState<
    ContactWorkbenchOrganizationOption[]
  >(() =>
    buildContactFilterSchoolOptionsFromSelection(
      runtime.draftFilters.orgIds,
      runtime.draftFilters.orgNames,
    ),
  );
  const [filterSchoolLoading, setFilterSchoolLoading] = useState(false);
  const filterSchoolRequestRef = useRef(0);
  const provinceFilterOptions = useMemo(() => getProvinceOptions(), []);
  const selectedFilterSchoolIds = runtime.draftFilters.orgIds ?? [];
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [selectionMode, setSelectionMode] =
    useState<ContactWorkbenchSelectionMode>("selected_rows");
  const [visibleColumns, setVisibleColumns] = useState<ContactWorkbenchColumnKey[]>(
    () =>
      loadWorkbenchVisibleColumnsPreference(
        CONTACT_WORKBENCH_VISIBLE_COLUMNS_STORAGE_KEY,
        crmContactWorkbenchColumnOptions,
        crmContactWorkbenchDefaultVisibleColumns,
      ),
  );
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [targetOwnerUserId, setTargetOwnerUserId] = useState<number>();
  const [sharedOwnerModalVisible, setSharedOwnerModalVisible] = useState(false);
  const [targetSharedOwnerUserIds, setTargetSharedOwnerUserIds] = useState<number[]>([]);
  const [sharedOwnerEditContext, setSharedOwnerEditContext] =
    useState<SharedOwnerEditContext | null>(null);
  const [sharedOwnerDeleteConfirm, setSharedOwnerDeleteConfirm] =
    useState<SharedOwnerDeleteContext | null>(null);
  const [temporaryTaskEditContext, setTemporaryTaskEditContext] =
    useState<TemporaryTaskEditContext | null>(null);
  const [targetTemporaryTaskUserId, setTargetTemporaryTaskUserId] = useState<number>();
  const [schoolBatchAssignmentVisible, setSchoolBatchAssignmentVisible] = useState(false);
  const [schoolBatchAssignmentMode, setSchoolBatchAssignmentMode] =
    useState<ContactWorkbenchSchoolBatchAssignmentMode>("join_task_pool");
  const [schoolBatchAssignmentSchoolIds, setSchoolBatchAssignmentSchoolIds] = useState<number[]>(
    [],
  );
  const [schoolBatchAssignmentSchools, setSchoolBatchAssignmentSchools] = useState<
    ContactWorkbenchOrganizationOption[]
  >([]);
  const [schoolBatchAssignmentScopeSchools, setSchoolBatchAssignmentScopeSchools] = useState<
    ContactWorkbenchOrganizationOption[]
  >([]);
  const [schoolBatchAssignmentSelectedProvinces, setSchoolBatchAssignmentSelectedProvinces] =
    useState<string[]>([]);
  const [schoolBatchAssignmentSelectedCityKeys, setSchoolBatchAssignmentSelectedCityKeys] =
    useState<string[]>([]);
  const [schoolBatchAssignmentTargetUserIds, setSchoolBatchAssignmentTargetUserIds] = useState<
    number[]
  >([]);
  const [schoolBatchAssignmentTaskName, setSchoolBatchAssignmentTaskName] = useState("");
  const [schoolBatchAssignmentContactCount, setSchoolBatchAssignmentContactCount] = useState(0);
  const [schoolBatchAssignmentSelectedNameFilter, setSchoolBatchAssignmentSelectedNameFilter] =
    useState("");
  const [schoolBatchAssignmentSearchKeyword, setSchoolBatchAssignmentSearchKeyword] =
    useState("");
  const [schoolNamePasteModalVisible, setSchoolNamePasteModalVisible] = useState(false);
  const [schoolNamePasteText, setSchoolNamePasteText] = useState("");
  const [schoolNamePasteApplying, setSchoolNamePasteApplying] = useState(false);
  const [schoolBatchPasteModalVisible, setSchoolBatchPasteModalVisible] = useState(false);
  const [schoolBatchPasteText, setSchoolBatchPasteText] = useState("");
  const [schoolBatchPasteApplying, setSchoolBatchPasteApplying] = useState(false);
  const [schoolBatchAssignmentScopeLoading, setSchoolBatchAssignmentScopeLoading] =
    useState(false);
  const [taskPoolModalVisible, setTaskPoolModalVisible] = useState(false);
  const [taskPoolType, setTaskPoolType] = useState<ContactWorkbenchTaskPoolType>("tele_sales");
  const [createDraft, setCreateDraft] = useState<ContactEditorDraft | null>(null);
  const [taskPoolName, setTaskPoolName] = useState("");
  const [taskPoolSubmitting, setTaskPoolSubmitting] = useState(false);
  const [contactDraft, setContactDraft] = useState<ContactEditorDraft | null>(null);
  const [recordDraft, setRecordDraft] = useState<ContactRecordDraft>(
    createDefaultContactRecordDraft,
  );
  /** 窄屏详情抽屉：修改资料 / 动态列表 / 仅新增动态；桌面仍展示完整三栏。 */
  const [mobileContactDrawerMode, setMobileContactDrawerMode] = useState<
    "edit" | "records" | "record"
  >("edit");
  const currentPageContactIds = useMemo(
    () => runtime.items.map((item) => item.id),
    [runtime.items],
  );
  const displayedSelectedRowKeys =
    selectionMode === "all_results" ? currentPageContactIds : selectedRowKeys;
  const selectedContactCount =
    selectionMode === "all_results" ? runtime.total : selectedRowKeys.length;
  const hasSelectedContacts = selectedContactCount > 0;
  const currentPageScopeActive = isCurrentPageSelectionActive(
    selectionMode,
    selectedRowKeys,
    currentPageContactIds,
  );
  const allResultsScopeActive = selectionMode === "all_results";
  const isBrowseAllScope = (runtime.submittedFilters.viewScope ?? "mine") === "all";
  const canCreateContact = canUsePermission(permissionUser, "crm.contact:create");
  const selectedItems = runtime.items.filter((item) => selectedRowKeys.includes(item.id));
  const selectedDataCanAssign =
    selectionMode === "all_results" ||
    (selectedItems.length > 0 && selectedItems.every((item) => item.canAssign));
  const selectedDataCanPutIntoTaskPool =
    selectionMode === "all_results" ||
    (selectedItems.length > 0 && selectedItems.every((item) => item.canPutIntoTaskPool));
  const canAssignSelectedContacts =
    !isBrowseAllScope &&
    hasSelectedContacts &&
    canAccessCrmOperation(permissionUser, "crm.contact:assign", selectedDataCanAssign);
  const canRegionSchoolBatchAssign = Boolean(
    runtime.metadata?.canRegionSchoolBatchAssign &&
      canUsePermission(permissionUser, "crm.contact:assign"),
  );
  const canPutSelectedContactsIntoTaskPool =
    !isBrowseAllScope &&
    hasSelectedContacts &&
    canAccessCrmOperation(
      permissionUser,
      "crm.collaboration:manage",
      selectedDataCanPutIntoTaskPool,
    );
  const selectedGiftContact = runtime.items.find((item) => item.id === runtime.giftContactId);
  const canManageSelectedGift = canAccessCrmOperation(
    permissionUser,
    "crm.contact:gift",
    selectedGiftContact?.canManageGift ?? false,
  );
  const selectedContactItem = runtime.items.find(
    (item) => item.id === runtime.selectedContactId,
  );
  const canEditSelectedContact = canAccessCrmOperation(
    permissionUser,
    "crm.contact:access",
    Boolean(selectedContactItem?.canEdit),
  );
  const canRecordSelectedContact = canAccessCrmOperation(
    permissionUser,
    "crm.contact:record",
    Boolean(selectedContactItem?.canRecord),
  );
  const canDeleteSelectedContactRecord = canAccessCrmOperation(
    permissionUser,
    "crm.contact:record",
    Boolean(selectedContactItem?.canDeleteRecord),
  );
  const currentOperatorName = useMemo(
    () => resolveCurrentOperatorName(runtime.staffOptions, currentUserQuery.data?.id ?? 0),
    [currentUserQuery.data?.id, runtime.staffOptions],
  );
  const schoolBatchAssignmentRegionOptions = useMemo(
    () => buildSchoolBatchAssignmentRegionOptions(schoolBatchAssignmentScopeSchools),
    [schoolBatchAssignmentScopeSchools],
  );
  const schoolBatchAssignmentMatchedRegionSchools = useMemo(
    () =>
      filterSchoolsByBatchAssignmentRegions(
        schoolBatchAssignmentScopeSchools,
        schoolBatchAssignmentSelectedProvinces,
        schoolBatchAssignmentSelectedCityKeys,
      ),
    [
      schoolBatchAssignmentScopeSchools,
      schoolBatchAssignmentSelectedCityKeys,
      schoolBatchAssignmentSelectedProvinces,
    ],
  );

  /** 按关键词远程模糊搜索学校（与省份筛选独立）。 */
  async function handleSchoolFilterSearch(keyword: string) {
    const requestId = filterSchoolRequestRef.current + 1;
    filterSchoolRequestRef.current = requestId;
    setFilterSchoolLoading(true);
    try {
      const options = await searchContactWorkbenchOrganizations({
        keyword: keyword.trim() || undefined,
        limit: 50,
      });
      if (requestId !== filterSchoolRequestRef.current) {
        return;
      }
      setFilterSchoolOptions((previous) =>
        mergeContactFilterSchoolOptions(options, selectedFilterSchoolIds, previous),
      );
    } catch {
      if (requestId !== filterSchoolRequestRef.current) {
        return;
      }
      Message.error("搜索学校失败，请稍后重试");
    } finally {
      if (requestId === filterSchoolRequestRef.current) {
        setFilterSchoolLoading(false);
      }
    }
  }

  /** 更新省份多选；不联动学校。 */
  function handleProvinceFilterChange(values: string[]) {
    const provinces = Array.isArray(values) ? values.map(String) : [];
    runtime.setDraftFilters((current) => ({
      ...current,
      provinces,
    }));
  }

  /** 更新学校多选筛选条件。 */
  function handleSchoolFilterChange(values: (string | number)[]) {
    const orgIds = (Array.isArray(values) ? values : [])
      .map((value) => Number(value))
      .filter((value) => Number.isSafeInteger(value) && value > 0);
    runtime.setDraftFilters((current) => ({
      ...current,
      orgIds,
      orgNames: resolveContactFilterOrgNames(orgIds, filterSchoolOptions, current.orgNames),
    }));
  }

  /** 按粘贴校名精确匹配并写入学校筛。 */
  async function applyPastedSchoolNames(names: string[]) {
    const uniqueNames = Array.from(new Set(names.map((name) => name.trim()).filter(Boolean)));
    if (!uniqueNames.length) {
      Message.warning("未识别到有效校名，请按换行或逗号粘贴");
      return;
    }
    setSchoolNamePasteApplying(true);
    try {
      const local = matchOrganizationsByExactNames(uniqueNames, filterSchoolOptions);
      const remoteMatched: ContactWorkbenchOrganizationOption[] = [];
      const stillMissing: string[] = [];
      for (const name of local.missing) {
        try {
          const candidates = await searchContactWorkbenchOrganizations({
            keyword: name,
            limit: 20,
          });
          const hit = pickBestOrganizationMatch(name, candidates);
          if (hit) {
            remoteMatched.push(hit);
          } else {
            stillMissing.push(name);
          }
        } catch {
          stillMissing.push(name);
        }
      }
      const matched = [...local.matched, ...remoteMatched];
      if (!matched.length) {
        Message.warning(
          stillMissing.length
            ? `未匹配到学校：${stillMissing.slice(0, 5).join("、")}${stillMissing.length > 5 ? " 等" : ""}`
            : "未匹配到学校",
        );
        return;
      }
      const nextOrgIds = Array.from(
        new Set([...(runtime.draftFilters.orgIds ?? []), ...matched.map((item) => item.id)]),
      );
      const mergedOptions = mergeContactFilterSchoolOptions(
        [...filterSchoolOptions, ...matched],
        nextOrgIds,
        filterSchoolOptions,
      );
      setFilterSchoolOptions(mergedOptions);
      const nextOrgNames = resolveContactFilterOrgNames(nextOrgIds, mergedOptions, [
        ...(runtime.draftFilters.orgNames ?? []),
        ...matched.map((item) => item.orgName),
      ]);
      runtime.setDraftFilters((prev) => ({
        ...prev,
        orgIds: nextOrgIds,
        orgNames: nextOrgNames,
      }));
      if (stillMissing.length) {
        Message.warning(
          `已选中 ${matched.length} 所学校；未匹配：${stillMissing.slice(0, 5).join("、")}${stillMissing.length > 5 ? " 等" : ""}`,
        );
      } else {
        Message.success(`已选中 ${matched.length} 所学校`);
      }
      setSchoolNamePasteModalVisible(false);
      setSchoolNamePasteText("");
    } finally {
      setSchoolNamePasteApplying(false);
    }
  }

  /** 学校筛粘贴：多行/分隔列表时批量识别校名。 */
  function handleSchoolFilterPaste(event: {
    clipboardData: DataTransfer;
    preventDefault: () => void;
    stopPropagation: () => void;
  }) {
    const text = event.clipboardData.getData("text");
    const names = splitPastedSchoolNames(text);
    const looksLikeBatch = names.length > 1 || /[\n\r,，、;；]/.test(text);
    if (!looksLikeBatch) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    void applyPastedSchoolNames(names);
  }

  /** 用当前筛选条件发起普通查询。 */
  function handleFilterSearch() {
    rememberSearchHistory(
      SEARCH_HISTORY_KEYS.contactKeyword,
      runtime.draftFilters.keyword ?? "",
    );
    runtime.handleSearch();
  }

  /** 用当前筛选条件发起智能查询。 */
  function handleFilterSmartSearch() {
    rememberSearchHistory(
      SEARCH_HISTORY_KEYS.contactNlQuery,
      runtime.draftFilters.nlQuery ?? "",
    );
    void runtime.handleSmartSearch();
  }

  /** 重置筛选条件与学校候选。 */
  function handleFilterReset() {
    setFilterSchoolOptions([]);
    runtime.handleReset();
  }
  const schoolBatchAssignmentPendingRegionSchools = useMemo(() => {
    const selectedIds = new Set(schoolBatchAssignmentSchoolIds);
    return schoolBatchAssignmentMatchedRegionSchools.filter((school) => !selectedIds.has(school.id));
  }, [schoolBatchAssignmentMatchedRegionSchools, schoolBatchAssignmentSchoolIds]);
  const schoolBatchAssignmentSelectedSchools = useMemo(() => {
    const schoolById = new Map(
      [...schoolBatchAssignmentScopeSchools, ...schoolBatchAssignmentSchools].map((school) => [
        school.id,
        school,
      ]),
    );
    return schoolBatchAssignmentSchoolIds
      .map((schoolId) => schoolById.get(schoolId))
      .filter((school): school is ContactWorkbenchOrganizationOption => Boolean(school));
  }, [
    schoolBatchAssignmentSchoolIds,
    schoolBatchAssignmentSchools,
    schoolBatchAssignmentScopeSchools,
  ]);
  const schoolBatchAssignmentVisibleSelectedSchools = useMemo(() => {
    const keyword = schoolBatchAssignmentSelectedNameFilter.trim().toLowerCase();
    if (!keyword) {
      return schoolBatchAssignmentSelectedSchools;
    }
    return schoolBatchAssignmentSelectedSchools.filter((school) =>
      school.orgName.toLowerCase().includes(keyword),
    );
  }, [schoolBatchAssignmentSelectedNameFilter, schoolBatchAssignmentSelectedSchools]);
  const schoolBatchAssignmentSelectOptions = useMemo(() => {
    return Array.from(
      new Map(
        [...schoolBatchAssignmentScopeSchools, ...schoolBatchAssignmentSchools].map((school) => [
          school.id,
          school,
        ]),
      ).values(),
    );
  }, [schoolBatchAssignmentSchools, schoolBatchAssignmentScopeSchools]);
  const columns = useMemo(
    () =>
      buildContactColumns({
        visibleColumns,
        latestRecordLabels: runtime.latestRecordLabels,
        latestRecordLabelsLoading: runtime.latestRecordLabelsLoading,
        giftCountsLoading: runtime.giftCountsLoading,
        permissionUser,
        sortField: runtime.sortField,
        sortDirection: runtime.sortDirection,
        onSort: runtime.handleSort,
        onOpenContact: runtime.handleOpenContact,
        onOpenQuickOpportunity: runtime.handleOpenQuickOpportunity,
        onOpenGifts: runtime.handleOpenGifts,
        onOpenSharedOwnerEditor: handleOpenSharedOwnerEditor,
        onRemoveSharedOwner: handleRemoveSharedOwner,
        onEditTemporaryTask: handleOpenTemporaryTaskEditor,
        onWithdrawTemporaryTask: handleWithdrawUnclaimedTemporaryTask,
        ownerFilterOptions: runtime.ownerOptions,
        ownerFilterLoading: runtime.ownerOptionsLoading,
        ownerUserIds: runtime.submittedFilters.ownerUserIds ?? [],
        sharedOwnerUserIds: runtime.submittedFilters.sharedOwnerUserIds ?? [],
        temporaryHandlerUserIds: runtime.submittedFilters.temporaryHandlerUserIds ?? [],
        onApplyOwnerFilters: runtime.handleApplyOwnerFilters,
      }),
    [
      handleOpenSharedOwnerEditor,
      permissionUser,
      handleRemoveSharedOwner,
      handleWithdrawUnclaimedTemporaryTask,
      runtime.handleApplyOwnerFilters,
      runtime.handleOpenContact,
      runtime.ownerOptions,
      runtime.ownerOptionsLoading,
      runtime.submittedFilters.ownerUserIds,
      runtime.submittedFilters.sharedOwnerUserIds,
      runtime.submittedFilters.temporaryHandlerUserIds,
      runtime.handleOpenQuickOpportunity,
      runtime.handleOpenGifts,
      runtime.latestRecordLabels,
      runtime.latestRecordLabelsLoading,
      runtime.giftCountsLoading,
      runtime.handleSort,
      runtime.sortDirection,
      runtime.sortField,
      visibleColumns,
    ],
  );

  const tableScrollWidth = columns.reduce(
    (total, column) => total + (typeof column.width === "number" ? column.width : 0),
    0,
  );

  const handleVisibleColumnsChange = useCallback((columns: ContactWorkbenchColumnKey[]) => {
    const normalizedColumns = normalizeWorkbenchVisibleColumns(
      columns,
      crmContactWorkbenchColumnOptions,
      crmContactWorkbenchDefaultVisibleColumns,
    );
    setVisibleColumns(normalizedColumns);
    saveWorkbenchVisibleColumnsPreference(
      CONTACT_WORKBENCH_VISIBLE_COLUMNS_STORAGE_KEY,
      normalizedColumns,
    );
  }, []);

  const findTableScroller = useCallback(() => {
    const tableShell = tableShellRef.current;
    // 同时设置 scroll.x/y 时，横向滚动发生在 table-body；仅 x 时发生在 content-inner。
    return (
      tableShell?.querySelector<HTMLElement>(".arco-table-body") ??
      tableShell?.querySelector<HTMLElement>(".arco-table-content-inner") ??
      tableShell?.querySelector<HTMLElement>(".arco-table-content-scroll") ??
      null
    );
  }, []);

  const syncScrollLeft = useCallback((source: HTMLElement, target: HTMLElement) => {
    if (syncingScrollRef.current) {
      return;
    }
    syncingScrollRef.current = true;
    target.scrollLeft = source.scrollLeft;
    window.requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  }, []);

  const handleTopScroll = useCallback(() => {
    const topScroll = topScrollRef.current;
    const tableScroller = findTableScroller();
    if (!topScroll || !tableScroller) {
      return;
    }
    syncScrollLeft(topScroll, tableScroller);
  }, [findTableScroller, syncScrollLeft]);

  useEffect(() => {
    const topScroll = topScrollRef.current;
    const tableScroller = findTableScroller();
    if (!topScroll || !tableScroller) {
      return undefined;
    }

    const updateMetrics = () => {
      const nextWidth = tableScroller.scrollWidth;
      const nextHasOverflow = stabilizeHorizontalOverflow(
        hasHorizontalOverflowRef.current,
        nextWidth,
        tableScroller.clientWidth,
      );
      hasHorizontalOverflowRef.current = nextHasOverflow;
      setTopScrollWidth((previous) =>
        previous === nextWidth ? previous : nextWidth,
      );
      setHasHorizontalOverflow((previous) =>
        previous === nextHasOverflow ? previous : nextHasOverflow,
      );
      if (nextHasOverflow) {
        if (topScroll.scrollLeft !== tableScroller.scrollLeft) {
          topScroll.scrollLeft = tableScroller.scrollLeft;
        }
      } else if (topScroll.scrollLeft !== 0) {
        topScroll.scrollLeft = 0;
      }
    };
    const handleTableScroll = () => syncScrollLeft(tableScroller, topScroll);
    tableScroller.addEventListener("scroll", handleTableScroll);
    window.addEventListener("resize", updateMetrics);
    updateMetrics();

    const observer =
      typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(updateMetrics);
    observer?.observe(tableScroller);
    if (tableScroller.firstElementChild instanceof HTMLElement) {
      observer?.observe(tableScroller.firstElementChild);
    }

    return () => {
      tableScroller.removeEventListener("scroll", handleTableScroll);
      window.removeEventListener("resize", updateMetrics);
      observer?.disconnect();
    };
    // 不用 columns 对象作依赖：runtime handler 每轮新引用会导致 effect 每帧重建并配合 setState 打满更新深度。
  }, [findTableScroller, runtime.items.length, syncScrollLeft, tableScrollWidth]);
  useEffect(() => {
    const tableShell = tableShellRef.current;
    if (!tableShell) {
      return undefined;
    }

    const updateTableScrollY = () => {
      const table = tableShell.querySelector(".arco-table");
      if (!(table instanceof HTMLElement)) {
        return;
      }
      const top = table.getBoundingClientRect().top;
      const pagination = table.querySelector(".arco-pagination");
      const paginationHeight =
        pagination instanceof HTMLElement ? pagination.getBoundingClientRect().height : 52;
      const nextScrollY = Math.max(
        320,
        Math.floor(window.innerHeight - top - paginationHeight - 12),
      );
      setTableScrollY((previous) => stabilizeLayoutMetric(previous, nextScrollY));
    };

    updateTableScrollY();
    window.addEventListener("resize", updateTableScrollY);
    return () => window.removeEventListener("resize", updateTableScrollY);
  }, [advancedFiltersVisible, runtime.items.length, tableScrollWidth]);
  useEffect(() => {
    if (!runtime.selectedContactDetail) {
      setContactDraft(null);
      return;
    }
    setContactDraft(createContactDraft(runtime.selectedContactDetail));
  }, [runtime.selectedContactDetail]);

  useEffect(() => {
    const firstTypeCode = runtime.metadata?.recordTypeOptions[0]?.code;
    if (!firstTypeCode) {
      return;
    }
    const hasCurrentType = runtime.metadata?.recordTypeOptions.some(
      (option) => option.code === recordDraft.recordTypeCode,
    );
    if (!hasCurrentType) {
      setRecordDraft((current) => ({ ...current, recordTypeCode: firstTypeCode }));
    }
  }, [recordDraft.recordTypeCode, runtime.metadata?.recordTypeOptions]);

  useEffect(() => {
    setSelectionMode("selected_rows");
    setSelectedRowKeys([]);
  }, [runtime.submittedFilters, runtime.sortDirection, runtime.sortField]);

  /** 窄屏默认每页 5 条，避免卡片列表一次加载过多。 */
  useEffect(() => {
    if (!isMobileViewport) {
      return;
    }
    if (runtime.pageSize === CONTACT_WORKBENCH_MOBILE_PAGE_SIZE) {
      return;
    }
    runtime.handlePageSizeChange(CONTACT_WORKBENCH_MOBILE_PAGE_SIZE);
  }, [isMobileViewport, runtime.pageSize]);

  /** 选择当前页面已经加载出的联系人，并与既有跨页勾选合并。 */
  function handleSelectCurrentPage() {
    setSelectionMode("selected_rows");
    setSelectedRowKeys((previous) => mergeContactSelectionKeys(previous, currentPageContactIds));
  }

  /** 选择当前查询条件下命中的全部联系人。 */
  function handleSelectAllResults() {
    if (runtime.total === 0) {
      return;
    }
    setSelectionMode("all_results");
    setSelectedRowKeys(currentPageContactIds);
  }

  /** 清空当前批量选择状态。 */
  function handleClearSelection() {
    setSelectionMode("selected_rows");
    setSelectedRowKeys([]);
  }

  /** 将当前选择状态解析为可提交的联系人 ID 列表。 */
  async function resolveSelectedContactIds() {
    return runtime.handleResolveSelectedContactIds({
      mode: selectionMode,
      contactIds: selectedRowKeys,
    });
  }

  /** 提交批量转移联系人归属销售。 */
  async function handleConfirmTransfer() {
    if (!targetOwnerUserId || !hasSelectedContacts) {
      return;
    }
    const contactIds = await resolveSelectedContactIds();
    await runtime.handleTransferOwner(contactIds, targetOwnerUserId);
    handleClearSelection();
    setTargetOwnerUserId(undefined);
    setTransferModalVisible(false);
  }

  /** 加载省市批量选择所需的完整区域学校范围。 */
  async function loadSchoolBatchAssignmentOrganizationScope() {
    const requestId = ++schoolBatchAssignmentScopeRequestRef.current;
    setSchoolBatchAssignmentScopeLoading(true);
    try {
      const options = await runtime.fetchSchoolBatchAssignmentOrganizationScope();
      if (requestId === schoolBatchAssignmentScopeRequestRef.current) {
        setSchoolBatchAssignmentScopeSchools(options);
      }
    } catch (error) {
      if (requestId === schoolBatchAssignmentScopeRequestRef.current) {
        Message.error(formatContactWorkbenchError(error, "区域学校完整范围加载失败，请稍后重试"));
      }
    } finally {
      if (requestId === schoolBatchAssignmentScopeRequestRef.current) {
        setSchoolBatchAssignmentScopeLoading(false);
      }
    }
  }

  /** 打开按学校批量分配抽屉。 */
  function handleOpenSchoolBatchAssignment() {
    setSchoolBatchAssignmentVisible(true);
    setSchoolBatchAssignmentMode("join_task_pool");
    setSchoolBatchAssignmentSchoolIds([]);
    setSchoolBatchAssignmentSchools([]);
    setSchoolBatchAssignmentScopeSchools([]);
    setSchoolBatchAssignmentSelectedProvinces([]);
    setSchoolBatchAssignmentSelectedCityKeys([]);
    setSchoolBatchAssignmentTargetUserIds([]);
    setSchoolBatchAssignmentTaskName(
      createDefaultTaskPoolName("tele_sales", 0, currentOperatorName),
    );
    setSchoolBatchAssignmentContactCount(0);
    setSchoolBatchAssignmentSelectedNameFilter("");
    setSchoolBatchAssignmentSearchKeyword("");
    setSchoolBatchPasteModalVisible(false);
    setSchoolBatchPasteText("");
    setSchoolBatchPasteApplying(false);
    schoolBatchAssignmentSchoolIdsRef.current = [];
    void loadSchoolBatchAssignmentOrganizationScope();
  }

  /** 搜索补充学校只负责追加勾选，不在输入框回填已选标签。 */
  function handleSchoolBatchAssignmentSearchPick(nextValue: (string | number)[]) {
    const pickedIds = nextValue.map(Number).filter(Number.isFinite);
    if (pickedIds.length === 0) {
      return;
    }
    void handleSchoolBatchAssignmentSchoolsChange([
      ...schoolBatchAssignmentSchoolIdsRef.current,
      ...pickedIds,
    ]);
  }

  /** 按粘贴校名精确匹配并追加到批量分配已选学校。 */
  async function applyPastedSchoolNamesToBatchAssignment(names: string[]) {
    const uniqueNames = Array.from(new Set(names.map((name) => name.trim()).filter(Boolean)));
    if (!uniqueNames.length) {
      Message.warning("未识别到有效校名，请按换行或逗号粘贴");
      return;
    }
    setSchoolBatchPasteApplying(true);
    try {
      const localCandidates = Array.from(
        new Map(
          [...schoolBatchAssignmentScopeSchools, ...schoolBatchAssignmentSchools].map((school) => [
            school.id,
            school,
          ]),
        ).values(),
      );
      const local = matchOrganizationsByExactNames(uniqueNames, localCandidates);
      const remoteMatched: ContactWorkbenchOrganizationOption[] = [];
      const stillMissing: string[] = [];
      for (const name of local.missing) {
        try {
          const candidates = await runtime.searchSchoolBatchAssignmentOrganizations(name);
          const hit = pickBestOrganizationMatch(name, candidates);
          if (hit) {
            remoteMatched.push(hit);
          } else {
            stillMissing.push(name);
          }
        } catch {
          stillMissing.push(name);
        }
      }
      const matched = [...local.matched, ...remoteMatched];
      if (!matched.length) {
        Message.warning(
          stillMissing.length
            ? `未匹配到学校：${stillMissing.slice(0, 5).join("、")}${stillMissing.length > 5 ? " 等" : ""}`
            : "未匹配到学校",
        );
        return;
      }
      setSchoolBatchAssignmentSchools((current) =>
        Array.from(
          new Map([...current, ...matched].map((school) => [school.id, school])).values(),
        ),
      );
      await handleSchoolBatchAssignmentSchoolsChange([
        ...schoolBatchAssignmentSchoolIdsRef.current,
        ...matched.map((item) => item.id),
      ]);
      setSchoolBatchAssignmentSearchKeyword("");
      if (stillMissing.length) {
        Message.warning(
          `已加入 ${matched.length} 所学校；未匹配：${stillMissing.slice(0, 5).join("、")}${stillMissing.length > 5 ? " 等" : ""}`,
        );
      } else {
        Message.success(`已加入 ${matched.length} 所学校`);
      }
      setSchoolBatchPasteModalVisible(false);
      setSchoolBatchPasteText("");
    } finally {
      setSchoolBatchPasteApplying(false);
    }
  }

  /** 批量分配搜索框粘贴：多行/分隔列表时批量识别校名。 */
  function handleSchoolBatchAssignmentPaste(event: {
    clipboardData: DataTransfer;
    preventDefault: () => void;
    stopPropagation: () => void;
  }) {
    const text = event.clipboardData.getData("text");
    const names = splitPastedSchoolNames(text);
    const looksLikeBatch = names.length > 1 || /[\n\r,，、;；]/.test(text);
    if (!looksLikeBatch) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    void applyPastedSchoolNamesToBatchAssignment(names);
  }

  /** 更新学校选择并刷新联系人数量统计。 */
  async function handleSchoolBatchAssignmentSchoolsChange(nextIds: (string | number)[]) {
    const ids = Array.from(new Set(nextIds.map(Number).filter(Number.isFinite)));
    const requestId = ++schoolBatchAssignmentPreviewRequestRef.current;
    schoolBatchAssignmentSchoolIdsRef.current = ids;
    setSchoolBatchAssignmentSchoolIds(ids);
    setSchoolBatchAssignmentContactCount(0);
    if (ids.length === 0) {
      setSchoolBatchAssignmentTaskName(
        createDefaultTaskPoolName("tele_sales", 0, currentOperatorName),
      );
      return;
    }
    try {
      const result = await runtime.previewSchoolBatchAssignment(ids);
      if (requestId === schoolBatchAssignmentPreviewRequestRef.current) {
        setSchoolBatchAssignmentContactCount(result.contactCount);
        setSchoolBatchAssignmentTaskName(
          createDefaultTaskPoolName("tele_sales", result.contactCount, currentOperatorName),
        );
      }
    } catch (error) {
      if (requestId === schoolBatchAssignmentPreviewRequestRef.current) {
        setSchoolBatchAssignmentContactCount(0);
        setSchoolBatchAssignmentTaskName(
          createDefaultTaskPoolName("tele_sales", 0, currentOperatorName),
        );
        Message.error(formatContactWorkbenchError(error, "联系人数量统计失败，请稍后重试"));
      }
    }
  }

  /** 把所选省份与城市命中的学校加入最终学校清单。 */
  function handleAddSchoolBatchAssignmentRegionSchools() {
    if (schoolBatchAssignmentPendingRegionSchools.length === 0) {
      return;
    }
    setSchoolBatchAssignmentSchools((current) =>
      Array.from(
        new Map(
          [...current, ...schoolBatchAssignmentPendingRegionSchools].map((school) => [
            school.id,
            school,
          ]),
        ).values(),
      ),
    );
    void handleSchoolBatchAssignmentSchoolsChange([
      ...schoolBatchAssignmentSchoolIds,
      ...schoolBatchAssignmentPendingRegionSchools.map((school) => school.id),
    ]);
  }

  /** 关闭并清空按学校批量分配抽屉。 */
  function handleCloseSchoolBatchAssignment() {
    schoolBatchAssignmentPreviewRequestRef.current += 1;
    schoolBatchAssignmentScopeRequestRef.current += 1;
    setSchoolBatchAssignmentVisible(false);
    setSchoolBatchAssignmentSchoolIds([]);
    setSchoolBatchAssignmentSchools([]);
    setSchoolBatchAssignmentScopeSchools([]);
    setSchoolBatchAssignmentSelectedProvinces([]);
    setSchoolBatchAssignmentSelectedCityKeys([]);
    setSchoolBatchAssignmentTargetUserIds([]);
    setSchoolBatchAssignmentTaskName(
      createDefaultTaskPoolName("tele_sales", 0, currentOperatorName),
    );
    setSchoolBatchAssignmentContactCount(0);
    setSchoolBatchAssignmentSelectedNameFilter("");
    setSchoolBatchAssignmentSearchKeyword("");
    setSchoolBatchPasteModalVisible(false);
    setSchoolBatchPasteText("");
    setSchoolBatchPasteApplying(false);
    schoolBatchAssignmentSchoolIdsRef.current = [];
    setSchoolBatchAssignmentScopeLoading(false);
  }

  /** 发起二次确认并提交按学校批量分配。 */
  function handleSubmitSchoolBatchAssignment() {
    if (
      schoolBatchAssignmentSchoolIds.length === 0 ||
      schoolBatchAssignmentContactCount === 0
    ) {
      return;
    }
    if (
      schoolBatchAssignmentMode === "join_task_pool" &&
      !schoolBatchAssignmentTaskName.trim()
    ) {
      return;
    }
    if (
      schoolBatchAssignmentMode !== "join_task_pool" &&
      schoolBatchAssignmentTargetUserIds.length === 0
    ) {
      return;
    }
    const targetNames = schoolBatchAssignmentTargetUserIds
      .map((id) => runtime.staffOptions.find((option) => option.id === id))
      .filter((option): option is ContactWorkbenchStaffOption => Boolean(option))
      .map(formatStaffOptionName);
    const content =
      schoolBatchAssignmentMode === "join_task_pool"
        ? `是否要将这些学校的 ${schoolBatchAssignmentContactCount} 名联系人加入电销任务池？`
        : schoolBatchAssignmentMode === "transfer_primary"
          ? `是否要将这些学校的 ${schoolBatchAssignmentContactCount} 名联系人主销售转移给「${targetNames[0]}」？`
          : `是否要对这些学校的 ${schoolBatchAssignmentContactCount} 名联系人添加共享销售「${targetNames.join("、")}」？`;
    Modal.confirm({
      title: "确认批量分配",
      content,
      onOk: async () => {
        try {
          await runtime.confirmSchoolBatchAssignment({
            orgIds: schoolBatchAssignmentSchoolIds,
            assignmentMode: schoolBatchAssignmentMode,
            targetUserIds:
              schoolBatchAssignmentMode === "join_task_pool"
                ? []
                : schoolBatchAssignmentTargetUserIds,
            taskName:
              schoolBatchAssignmentMode === "join_task_pool"
                ? schoolBatchAssignmentTaskName.trim()
                : undefined,
          });
          handleCloseSchoolBatchAssignment();
        } catch {
          // 失败提示由 runtime mutation onError 展示；保留抽屉和选择供重试。
        }
      },
    });
  }

  /** 提交批量追加联系人共享销售。 */
  function handleConfirmSharedOwner() {
    if (sharedOwnerEditContext) {
      const editContext = sharedOwnerEditContext;
      const targetUserIds = [...targetSharedOwnerUserIds];
      setTargetSharedOwnerUserIds([]);
      setSharedOwnerEditContext(null);
      setSharedOwnerModalVisible(false);
      void runtime.handleReplaceContactSharedOwners(
        editContext.contactId,
        editContext.currentUserIds,
        targetUserIds,
      );
      return;
    }
    if (targetSharedOwnerUserIds.length === 0 || !hasSelectedContacts) {
      return;
    }
    const selectionSnapshot = {
      mode: selectionMode,
      contactIds: selectedRowKeys,
    };
    const targetUserIds = [...targetSharedOwnerUserIds];
    handleClearSelection();
    setTargetSharedOwnerUserIds([]);
    setSharedOwnerModalVisible(false);
    void runtime.handleAddSharedOwnersForSelection(selectionSnapshot, targetUserIds);
  }

  /** 打开单个联系人的共享销售修改弹窗。 */
  function handleOpenSharedOwnerEditor(item: ContactWorkbenchItem) {
    const sharedOwnerUserIds = item.sharedOwnerUserIds ?? [];
    setSelectionMode("selected_rows");
    setSelectedRowKeys([item.id]);
    setSharedOwnerEditContext({
      contactId: item.id,
      contactName: item.contactName,
      currentUserIds: sharedOwnerUserIds,
      currentUserNames: item.sharedOwnerUserNames ?? [],
    });
    setTargetSharedOwnerUserIds(sharedOwnerUserIds);
    setSharedOwnerModalVisible(true);
  }

  /** 移除单个联系人已有的共享销售。 */
  function handleRemoveSharedOwner(item: ContactWorkbenchItem, userId: number, userName: string) {
    setSharedOwnerDeleteConfirm({ item, userId, userName });
  }

  /** 确认移除单个联系人已有的共享销售。 */
  function handleConfirmRemoveSharedOwner() {
    if (!sharedOwnerDeleteConfirm) {
      return;
    }
    const { item, userId } = sharedOwnerDeleteConfirm;
    const currentUserIds = item.sharedOwnerUserIds ?? [];
    const targetUserIds = currentUserIds.filter((currentUserId) => currentUserId !== userId);
    setSharedOwnerDeleteConfirm(null);
    void runtime.handleReplaceContactSharedOwners(item.id, currentUserIds, targetUserIds);
  }

  /** 打开已选联系人投放任务池弹窗。 */
  function handleOpenTaskPoolModal() {
    if (!hasSelectedContacts) {
      return;
    }
    setTaskPoolType("tele_sales");
    setTaskPoolName(
      createDefaultTaskPoolName("tele_sales", selectedContactCount, currentOperatorName),
    );
    setTaskPoolModalVisible(true);
  }

  /** 确认把已选联系人投放到协作任务公共池。 */
  async function handleConfirmTaskPool() {
    if (
      !taskPoolName.trim() ||
      !hasSelectedContacts ||
      taskPoolSubmitting ||
      runtime.taskPoolMutating
    ) {
      return;
    }
    // 解析全部结果 ID 或大批量勾选可能耗时，先同步进入加载态再开始解析。
    flushSync(() => {
      setTaskPoolSubmitting(true);
    });
    try {
      const contactIds = await resolveSelectedContactIds();
      await runtime.handleCreateTaskPool({
        contactIds,
        taskType: taskPoolType,
        taskName: taskPoolName.trim(),
      });
    } catch {
      return;
    } finally {
      setTaskPoolSubmitting(false);
    }
    handleClearSelection();
    setTaskPoolModalVisible(false);
  }

  /** 打开单个联系人任务项的临时处理人编辑器。 */
  function handleOpenTemporaryTaskEditor(
    item: ContactWorkbenchItem,
    assignment: ContactWorkbenchTemporaryTaskAssignment,
  ) {
    setTemporaryTaskEditContext({
      contactId: item.id,
      contactName: item.contactName,
      assignment,
    });
    setTargetTemporaryTaskUserId(assignment.handlerUserId);
  }

  /** 保存已选的临时任务处理人。 */
  async function handleConfirmTemporaryTaskHandler() {
    if (!temporaryTaskEditContext) {
      return;
    }
    const context = temporaryTaskEditContext;
    const handlerUserId = targetTemporaryTaskUserId;
    try {
      await runtime.handleUpdateTemporaryTaskHandler(
        context.contactId,
        context.assignment.itemId,
        handlerUserId,
      );
      setTemporaryTaskEditContext(null);
      setTargetTemporaryTaskUserId(undefined);
    } catch {
      return;
    }
  }

  /** 清空已认领任务的处理人并直接收回任务。 */
  async function handleClearAndWithdrawTemporaryTask() {
    if (!temporaryTaskEditContext) {
      return;
    }
    const context = temporaryTaskEditContext;
    try {
      await runtime.handleUpdateTemporaryTaskHandler(
        context.contactId,
        context.assignment.itemId,
        undefined,
      );
      setTemporaryTaskEditContext(null);
      setTargetTemporaryTaskUserId(undefined);
    } catch {
      return;
    }
  }

  /** 二次确认后收回尚未被认领的临时任务。 */
  function handleWithdrawUnclaimedTemporaryTask(
    item: ContactWorkbenchItem,
    assignment: ContactWorkbenchTemporaryTaskAssignment,
  ) {
    Modal.confirm({
      title: "收回临时任务",
      content: `确认收回「${assignment.taskName}」吗？收回后该任务将不再出现在任务池。`,
      okText: "确认收回",
      cancelText: "取消",
      okButtonProps: { status: "danger" },
      onOk: () => runtime.handleWithdrawTemporaryTask(item.id, assignment.itemId),
    });
  }

  /** 保存联系人编辑内容；窄屏成功后关闭抽屉。 */
  async function handleSaveContact() {
    if (
      !contactDraft?.orgId ||
      !contactDraft.contactName.trim() ||
      getMobileValidationError(contactDraft.mobile)
    ) {
      return;
    }
    const saved = await runtime.handleUpdateContact(mapContactDraftToPayload(contactDraft));
    if (saved) {
      closeContactDrawerAfterSave();
    }
  }

  /** 打开新增联系人抽屉。 */
  function openCreateContactDrawer() {
    runtime.handleCloseContact();
    setCreateDraft(createEmptyContactDraft());
  }

  /** 保存新增联系人。 */
  async function handleCreateContact() {
    if (
      !createDraft?.orgId ||
      !createDraft.contactName.trim() ||
      getMobileValidationError(createDraft.mobile)
    ) {
      return;
    }
    const created = await runtime.handleCreateContact(mapContactDraftToPayload(createDraft));
    if (created) {
      setCreateDraft(null);
    }
  }

  /** 新增联系人动态；窄屏成功后关闭抽屉。 */
  async function handleCreateRecord() {
    const payload = mapContactRecordDraftToPayload(recordDraft);
    if (!payload) {
      return;
    }
    try {
      await runtime.handleCreateRecord(payload);
      setRecordDraft(createDefaultContactRecordDraft());
      closeContactDrawerAfterSave();
    } catch {
      // mutation 已提示错误，保留抽屉便于修正后重试。
    }
  }

  /** 窄屏保存成功后关闭详情抽屉；桌面详情仍保持打开以便继续编辑或补录动态。 */
  function closeContactDrawerAfterSave() {
    if (!isMobileViewport) {
      return;
    }
    runtime.handleCloseContact();
    setRecordDraft(createDefaultContactRecordDraft());
    setMobileContactDrawerMode("edit");
  }

  /** 点击垃圾桶后直接删除联系人动态，不再二次确认。 */
  async function handleDeleteRecord(record: ContactWorkbenchRecordItem) {
    await runtime.handleDeleteRecord(record.id);
  }

  return (
    <main
      className={`crm-list-page crm-contact-workbench-page${isMobileViewport ? " is-mobile" : ""}`}
    >
      {isMobileViewport ? (
        <>
          <MobileContactFilterBar
            runtime={runtime}
            canCreate={!isBrowseAllScope && canCreateContact}
            onCreate={openCreateContactDrawer}
            onReset={handleFilterReset}
            schoolOptions={filterSchoolOptions}
            schoolLoading={filterSchoolLoading}
            onSearchSchools={handleSchoolFilterSearch}
            onSchoolChange={handleSchoolFilterChange}
          />
          <MobileContactSortBar
            sortField={runtime.sortField}
            sortDirection={runtime.sortDirection}
            onSort={runtime.handleSort}
          />
        </>
      ) : (
      <section
        className="crm-list-filter-panel crm-contact-workbench-filter-panel"
        role="search"
        aria-label="联系人筛选"
        data-layout="two-tier-responsive"
      >
        <div className="crm-list-filter-fields crm-contact-workbench-filter-fields">
          <div className="crm-list-filter-primary">
            <label className="crm-list-field crm-list-field--lg crm-contact-workbench-field">
              <span>关键词</span>
              <SearchHistoryInput
                storageKey={SEARCH_HISTORY_KEYS.contactKeyword}
                placeholder="联系人/手机/微信/学校/院系"
                value={runtime.draftFilters.keyword ?? ""}
                onChange={(keyword) =>
                  runtime.setDraftFilters((current) => ({ ...current, keyword }))
                }
                onPressEnter={handleFilterSearch}
              />
            </label>
            <div className="crm-list-field crm-list-field--sm crm-contact-workbench-field crm-contact-workbench-field--compact">
              <span>星级</span>
              <Select
                allowClear
                data-testid="contact-star-filter"
                placeholder="全部星级"
                triggerProps={{ autoFitPosition: true, position: "bl" }}
                getPopupContainer={() => document.body}
                dropdownMenuStyle={{ maxHeight: 320 }}
                value={resolveContactStarSelectValue(
                  runtime.draftFilters.contactStar,
                  runtime.draftFilters.contactStarUnset,
                )}
                onChange={(value) => {
                  const next = parseContactStarSelectValue(value);
                  runtime.setDraftFilters((current) => ({ ...current, ...next }));
                }}
              >
                <Select.Option value="__unset__">未设置星级</Select.Option>
                {resolveContactStarFilterOptions(runtime.metadata?.contactStarOptions).map((option) => (
                  <Select.Option key={option} value={option} title={formatContactStarOptionLabel(option)}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="crm-list-field crm-list-field--md crm-contact-workbench-field crm-contact-workbench-record-label-filter">
              <span>有出行</span>
              <Select
                allowClear
                showSearch
                mode="multiple"
                data-testid="contact-record-label-filter"
                className="crm-contact-workbench-compact-multi-select"
                placeholder="可多选出行标签"
                maxTagCount={1}
                triggerProps={{ autoFitPosition: true, position: "bl" }}
                getPopupContainer={() => document.body}
                dropdownMenuStyle={{ maxHeight: 320 }}
                value={runtime.draftFilters.recordLabels ?? []}
                filterOption={(inputValue, option) =>
                  String(option.props.searchText ?? "")
                    .toLowerCase()
                    .includes(inputValue.trim().toLowerCase())
                }
                onChange={(values) =>
                  runtime.setDraftFilters((current) => ({
                    ...current,
                    recordLabels: Array.isArray(values) ? values.map(String) : [],
                  }))
                }
              >
                {(runtime.metadata?.recordLabelGroups ?? [])
                  .filter((group) =>
                    group.groupCode === "travel"
                  )
                  .flatMap((group) =>
                    group.labels.map((option) => (
                      <Select.Option
                        key={`${group.groupCode}-${option.label}`}
                        value={option.label}
                        searchText={option.label}
                      >
                        {option.label}
                      </Select.Option>
                    )),
                  )}
              </Select>
            </div>
            <div className="crm-list-field crm-list-field--md crm-contact-workbench-field crm-contact-workbench-record-label-filter">
              <span>后续动作</span>
              <Select
                allowClear
                showSearch
                mode="multiple"
                data-testid="contact-follow-up-label-filter"
                className="crm-contact-workbench-compact-multi-select"
                placeholder="可多选后续动作"
                maxTagCount={1}
                triggerProps={{ autoFitPosition: true, position: "bl" }}
                getPopupContainer={() => document.body}
                dropdownMenuStyle={{ maxHeight: 320 }}
                value={runtime.draftFilters.followUpLabels ?? []}
                filterOption={(inputValue, option) =>
                  String(option.props.searchText ?? "")
                    .toLowerCase()
                    .includes(inputValue.trim().toLowerCase())
                }
                onChange={(values) =>
                  runtime.setDraftFilters((current) => ({
                    ...current,
                    followUpLabels: Array.isArray(values) ? values.map(String) : [],
                  }))
                }
              >
                {(runtime.metadata?.recordLabelGroups ?? [])
                  .filter((group) => group.groupCode === "follow_up")
                  .flatMap((group) =>
                    group.labels.map((option) => (
                      <Select.Option
                        key={`${group.groupCode}-${option.label}`}
                        value={option.label}
                        searchText={option.label}
                      >
                        {option.label}
                      </Select.Option>
                    )),
                  )}
              </Select>
            </div>
            <div className="crm-list-field crm-list-field--md crm-contact-workbench-field">
              <span>归属销售</span>
              <StaffSelect
                allowClear
                placeholder="全部销售"
                value={runtime.draftFilters.ownerUserId}
                options={mergeOwnerFilterStaffOptions(
                  runtime.staffOptions,
                  runtime.draftFilters.ownerUserId,
                  runtime.draftFilters.ownerUserName,
                )}
                getPopupContainer={() => document.body}
                dropdownMenuStyle={{ maxHeight: 320 }}
                onChange={(ownerUserId, ownerUserName) =>
                  runtime.setDraftFilters((current) => ({
                    ...current,
                    ownerUserId,
                    ownerUserName,
                    ownerUserIds: undefined,
                    sharedOwnerUserIds: undefined,
                    temporaryHandlerUserIds: undefined,
                  }))
                }
              />
            </div>
            <div className="crm-list-field crm-list-field--md crm-contact-workbench-field">
              <span>省份</span>
              <Select
                allowClear
                showSearch
                mode="multiple"
                data-testid="contact-province-filter"
                className="crm-contact-workbench-compact-multi-select"
                placeholder="可多选省份"
                maxTagCount={1}
                triggerProps={{ autoFitPosition: true, position: "bl" }}
                getPopupContainer={() => document.body}
                dropdownMenuStyle={{ maxHeight: 320 }}
                value={runtime.draftFilters.provinces ?? []}
                filterOption={(inputValue, option) =>
                  String(option.props.value ?? "")
                    .toLowerCase()
                    .includes(inputValue.trim().toLowerCase())
                }
                onChange={(values) =>
                  handleProvinceFilterChange(Array.isArray(values) ? values.map(String) : [])
                }
              >
                {provinceFilterOptions.map((province) => (
                  <Select.Option key={province} value={province}>
                    {province}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="crm-list-field crm-list-field--lg crm-contact-workbench-field">
              <span className="crm-contact-workbench-school-filter-label">
                学校
                <Button
                  type="text"
                  size="mini"
                  className="crm-contact-workbench-school-paste-trigger"
                  data-testid="contact-school-paste-trigger"
                  onClick={() => {
                    setSchoolNamePasteText("");
                    setSchoolNamePasteModalVisible(true);
                  }}
                >
                  粘贴校名
                </Button>
              </span>
              <div onPaste={handleSchoolFilterPaste}>
                <Select
                allowClear
                showSearch
                mode="multiple"
                data-testid="contact-school-filter"
                className="crm-contact-workbench-school-filter crm-contact-workbench-compact-multi-select"
                placeholder="可搜索学校"
                loading={filterSchoolLoading || schoolNamePasteApplying}
                maxTagCount={1}
                triggerProps={{ autoFitPosition: true, position: "bl" }}
                getPopupContainer={() => document.body}
                dropdownMenuStyle={{ maxHeight: 320 }}
                value={selectedFilterSchoolIds}
                filterOption={false}
                onSearch={(keyword) => {
                  void handleSchoolFilterSearch(keyword);
                }}
                onVisibleChange={(visible) => {
                  if (visible) {
                    void handleSchoolFilterSearch("");
                  }
                }}
                onChange={(values) =>
                  handleSchoolFilterChange(Array.isArray(values) ? values : [])
                }
              >
                {filterSchoolOptions.map((option) => (
                  <Select.Option
                    key={option.id}
                    value={option.id}
                    searchText={`${option.orgName} ${option.province ?? ""} ${option.city ?? ""}`}
                  >
                    {option.orgName}
                  </Select.Option>
                ))}
              </Select>
              </div>
            </div>
          </div>

          {advancedFiltersVisible ? (
            <div className="crm-list-filter-advanced crm-contact-workbench-advanced-filters">
              <label className="crm-list-field crm-list-field--sm crm-contact-workbench-field">
                <span>城市</span>
                <Input
                  allowClear
                  placeholder="例如：北京市"
                  value={runtime.draftFilters.city ?? ""}
                  onChange={(city) =>
                    runtime.setDraftFilters((current) => ({ ...current, city }))
                  }
                />
              </label>
              <div className="crm-list-field crm-list-field--sm crm-contact-workbench-field">
                <span>联系人状态</span>
                <Select
                  allowClear
                  placeholder="全部状态"
                  triggerProps={{ autoFitPosition: true, position: "bl" }}
                  getPopupContainer={() => document.body}
                  value={resolveStatusSelectValue(runtime.draftFilters.isActive)}
                  onChange={(value) =>
                    runtime.setDraftFilters((current) => ({
                      ...current,
                      isActive: parseStatusSelectValue(value),
                    }))
                  }
                >
                  <Select.Option value="true">在岗</Select.Option>
                  <Select.Option value="false">停用</Select.Option>
                </Select>
              </div>
              <div className="crm-contact-workbench-nl-query">
                <label className="crm-list-field crm-contact-workbench-field">
                  <span>智能查询</span>
                  <SearchHistoryInput
                    storageKey={SEARCH_HISTORY_KEYS.contactNlQuery}
                    placeholder="例如：重庆大学 张老师 / 浙江或江苏 艺术设计学院 张老师 / 最近半年有动态 没有手机号"
                    value={runtime.draftFilters.nlQuery ?? ""}
                    onChange={runtime.handleNlQueryChange}
                    onPressEnter={handleFilterSmartSearch}
                  />
                </label>
                <Button
                  icon={<Search size={16} />}
                  loading={runtime.nlQueryParsing}
                  onClick={handleFilterSmartSearch}
                >
                  智能查询
                </Button>
              </div>
            </div>
          ) : null}

          {runtime.nlQueryErrorMessage ? (
            <Alert
              className="crm-list-alert crm-contact-workbench-alert"
              type="error"
              showIcon
              content={runtime.nlQueryErrorMessage}
            />
          ) : null}
          <NlQuerySummary result={runtime.nlQueryParseResult} />
          {runtime.errorMessage ? (
            <Alert
              className="crm-list-alert crm-contact-workbench-alert"
              type="error"
              showIcon
              content={runtime.errorMessage}
            />
          ) : null}
        </div>
        <div className="crm-list-filter-actions crm-contact-workbench-actions">
          <CrmListFilterMoreButton
            expanded={advancedFiltersVisible}
            onToggle={() => setAdvancedFiltersVisible((current) => !current)}
          />
          <Button icon={<RotateCcw size={16} />} onClick={handleFilterReset}>
            重置
          </Button>
          <Button type="primary" icon={<Search size={16} />} onClick={handleFilterSearch}>
            查询
          </Button>
        </div>
      </section>
      )}
      {isMobileViewport ? (
        <MobileContactList
          runtime={runtime}
          permissionUser={permissionUser}
          latestRecordLabels={runtime.latestRecordLabels}
          latestRecordLabelsLoading={runtime.latestRecordLabelsLoading}
          onEdit={(item) => {
            if (!item.canOpenContact) {
              return;
            }
            setMobileContactDrawerMode("edit");
            runtime.handleOpenContact(item.id);
          }}
          onViewRecords={(item) => {
            if (!item.canOpenContact) {
              return;
            }
            setMobileContactDrawerMode("records");
            runtime.handleOpenContact(item.id);
          }}
          onAddRecord={(item) => {
            if (!item.canOpenContact) {
              return;
            }
            setMobileContactDrawerMode("record");
            setRecordDraft(createDefaultContactRecordDraft());
            runtime.handleOpenContact(item.id);
          }}
        />
      ) : (
      <section className="crm-list-panel crm-contact-workbench-table-card">
        <div className="crm-list-section-header crm-contact-workbench-table-header">
          <div className="crm-list-title-row">
            <h2>联系人列表</h2>
            <Radio.Group
              type="button"
              value={runtime.draftFilters.viewScope ?? "mine"}
              onChange={(value) => {
                const nextScope = value as "mine" | "all";
                if (nextScope === "all") {
                  setSelectionMode("selected_rows");
                  setSelectedRowKeys([]);
                }
                runtime.handleViewScopeChange(nextScope);
              }}
            >
              <Radio value="mine">我的联系人</Radio>
              <Radio value="all">全部联系人</Radio>
            </Radio.Group>
          </div>
          <div className="crm-list-table-actions">
              {!isBrowseAllScope && canCreateContact ? (
                <Button type="primary" icon={<Plus size={14} />} onClick={openCreateContactDrawer}>
                  新增联系人
                </Button>
              ) : null}
              <Button icon={<ClipboardList size={14} />} href="/crm/collaboration/pools">
                查看任务池
              </Button>
              <Popover
                position="br"
                trigger="click"
                content={
                  <WorkbenchColumnSettings
                    options={crmContactWorkbenchColumnOptions}
                    visibleColumns={visibleColumns}
                    onChange={handleVisibleColumnsChange}
                  />
                }
              >
                <Button icon={<Columns3 size={14} />}>列设置</Button>
              </Popover>
              {!isBrowseAllScope && canRegionSchoolBatchAssign ? (
                <Button icon={<UserRoundCheck size={14} />} onClick={handleOpenSchoolBatchAssignment}>
                  批量分配
                </Button>
              ) : null}
          </div>
        </div>

        <div className="crm-list-panel-body">
        {hasSelectedContacts && !isBrowseAllScope ? (
          <div className="crm-contact-workbench-selection-bar">
            <div className="crm-contact-workbench-selection-bar__summary">
              <span className="crm-contact-workbench-selection-bar__count">
                {selectionMode === "all_results"
                  ? `已选全部 ${formatContactCount(runtime.total)} 位`
                  : `已选 ${formatContactCount(selectedContactCount)} 位`}
              </span>
              <span className="crm-contact-workbench-selection-bar__hint">可扩展选择范围或执行批量操作</span>
            </div>
            <div className="crm-contact-workbench-selection-bar__scopes">
              <Button
                size="small"
                type={currentPageScopeActive ? "primary" : "secondary"}
                aria-pressed={currentPageScopeActive}
                onClick={handleSelectCurrentPage}
              >
                当前页 {formatContactCount(currentPageContactIds.length)} 位
              </Button>
              <Button
                size="small"
                type={allResultsScopeActive ? "primary" : "secondary"}
                aria-pressed={allResultsScopeActive}
                onClick={handleSelectAllResults}
              >
                全部结果 {formatContactCount(runtime.total)} 位
              </Button>
            </div>
            <div className="crm-contact-workbench-selection-bar__actions">
              {canPutSelectedContactsIntoTaskPool ? (
                <Button size="small" icon={<ClipboardList size={14} />} onClick={handleOpenTaskPoolModal}>
                  加入电销任务池
                </Button>
              ) : null}
              {canAssignSelectedContacts ? (
                <>
                  <Button
                    size="small"
                    icon={<UserRoundCheck size={14} />}
                    onClick={() => setSharedOwnerModalVisible(true)}
                  >
                    添加共享销售
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    icon={<UserRoundCheck size={14} />}
                    onClick={() => setTransferModalVisible(true)}
                  >
                    转移主销售
                  </Button>
                </>
              ) : null}
              <Button size="small" status="danger" type="outline" onClick={handleClearSelection}>
                清除选择
              </Button>
            </div>
          </div>
        ) : null}
        {runtime.sharedOwnerBatchNotice ? (
          <Alert
            className="crm-list-alert crm-contact-workbench-alert"
            type={runtime.sharedOwnerBatchNotice.type}
            showIcon
            content={runtime.sharedOwnerBatchNotice.content}
          />
        ) : null}

        <div className="crm-contact-workbench-table-shell" ref={tableShellRef}>
          <div
            className="crm-contact-workbench-top-scroll"
            data-testid="contact-workbench-top-scroll"
            ref={topScrollRef}
            aria-hidden={!hasHorizontalOverflow}
            onScroll={handleTopScroll}
          >
            <div
              className="crm-contact-workbench-top-scroll-inner"
              data-testid="contact-workbench-top-scroll-inner"
              style={{ width: topScrollWidth }}
            />
          </div>
          <Table
            className={
              isBrowseAllScope
                ? "crm-contact-workbench-table crm-contact-workbench-table--browse"
                : "crm-contact-workbench-table"
            }
            rowKey="id"
            columns={columns}
            data={runtime.items}
            loading={runtime.loading}
            border={false}
            scroll={{ x: tableScrollWidth, y: tableScrollY }}
            rowSelection={
              isBrowseAllScope
                ? undefined
                : {
                    selectedRowKeys: displayedSelectedRowKeys,
                    checkCrossPage: true,
                    preserveSelectedRowKeys: true,
                    onChange: (keys) => {
                      setSelectionMode("selected_rows");
                      setSelectedRowKeys(keys.map((key) => Number(key)));
                    },
                  }
            }
            pagination={{
              current: runtime.page,
              pageSize: runtime.pageSize,
              total: runtime.total,
              sizeCanChange: true,
              sizeOptions: [10, 20, 50, 100],
              showTotal: true,
              onChange: runtime.handlePageChange,
              onPageSizeChange: runtime.handlePageSizeChange,
            }}
          />
        </div>
        </div>
      </section>
      )}

      <ContactGiftModal
        visible={Boolean(runtime.giftContactId)}
        contactName={selectedGiftContact?.contactName ?? "联系人"}
        canManageGift={canManageSelectedGift}
        items={runtime.giftItems}
        total={runtime.giftTotal}
        page={runtime.giftPage}
        pageSize={runtime.giftPageSize}
        options={runtime.giftOptions}
        loading={runtime.giftLoading}
        error={runtime.giftError}
        mutating={runtime.giftMutating}
        onClose={runtime.handleCloseGifts}
        onRefresh={runtime.handleRefreshGifts}
        onPageChange={runtime.handleGiftPageChange}
        onCreate={runtime.handleCreateGift}
        onUpdate={runtime.handleUpdateGift}
        onDelete={runtime.handleDeleteGift}
      />
      <Modal
        title="转移联系人主销售"
        visible={transferModalVisible}
        footer={
          <Space>
            <Button
              onClick={() => {
                setTransferModalVisible(false);
                setTargetOwnerUserId(undefined);
              }}
            >
              取消
            </Button>
            <Button
              type="primary"
              loading={runtime.mutating}
              disabled={!targetOwnerUserId || !hasSelectedContacts}
              onClick={() => void handleConfirmTransfer()}
            >
              确定
            </Button>
          </Space>
        }
        onCancel={() => {
          setTransferModalVisible(false);
          setTargetOwnerUserId(undefined);
        }}
      >
        <div className="crm-contact-workbench-transfer-form">
          <p>{formatSelectionActionDescription(selectionMode, selectedContactCount, "转移")}</p>
          <StaffSelect
            placeholder="选择目标主销售"
            value={targetOwnerUserId}
            options={runtime.staffOptions}
            onChange={setTargetOwnerUserId}
          />
        </div>
      </Modal>

      <Modal
        title="添加共享销售/设置共享人"
        visible={sharedOwnerModalVisible}
        unmountOnExit
        footer={
          <Space>
            <Button
              onClick={() => {
                setSharedOwnerModalVisible(false);
                setTargetSharedOwnerUserIds([]);
                setSharedOwnerEditContext(null);
              }}
            >
              取消
            </Button>
            <Button
              type="primary"
              loading={runtime.mutating}
              disabled={
                sharedOwnerEditContext
                  ? false
                  : targetSharedOwnerUserIds.length === 0 || !hasSelectedContacts
              }
              onClick={handleConfirmSharedOwner}
            >
              确定
            </Button>
          </Space>
        }
        onCancel={() => {
          setSharedOwnerModalVisible(false);
          setTargetSharedOwnerUserIds([]);
          setSharedOwnerEditContext(null);
        }}
      >
        <div className="crm-contact-workbench-transfer-form">
          <p>
            {sharedOwnerEditContext
              ? `正在设置「${sharedOwnerEditContext.contactName}」的共享销售`
              : formatSelectionActionDescription(selectionMode, selectedContactCount, "共享")}
          </p>
          <p className="crm-contact-workbench-modal-hint">
            {sharedOwnerEditContext
              ? "可追加或移除共享销售，不改变联系人主销售。清空后确认会移除全部共享人。"
              : "仅添加共享销售，不改变联系人主销售。"}
          </p>
          {sharedOwnerEditContext ? (
            <Button
              size="small"
              disabled={targetSharedOwnerUserIds.length === 0}
              onClick={() => setTargetSharedOwnerUserIds([])}
            >
              清空共享人
            </Button>
          ) : null}
          <div data-testid="contact-shared-owner-select">
            <StaffMultiSelect
              placeholder="选择共享销售"
              value={targetSharedOwnerUserIds}
              options={runtime.staffOptions}
              onChange={setTargetSharedOwnerUserIds}
            />
          </div>
        </div>
      </Modal>

      <Modal
        title="投放任务池"
        visible={taskPoolModalVisible}
        footer={
          <Space>
            <Button onClick={() => setTaskPoolModalVisible(false)}>取消</Button>
            <Button
              type="primary"
              loading={taskPoolSubmitting || runtime.taskPoolMutating}
              disabled={
                !taskPoolName.trim() ||
                !hasSelectedContacts ||
                taskPoolSubmitting ||
                runtime.taskPoolMutating
              }
              onClick={() => void handleConfirmTaskPool()}
            >
              确定
            </Button>
          </Space>
        }
        onCancel={() => setTaskPoolModalVisible(false)}
      >
        <div className="crm-contact-workbench-task-pool-form">
          <p>{formatSelectionActionDescription(selectionMode, selectedContactCount, "投放")}</p>
          <div className="crm-contact-workbench-field">
            <span>选择任务池</span>
            <Radio.Group
              type="button"
              aria-label="选择任务池"
              value={taskPoolType}
              onChange={(nextValue) => {
                const nextTaskType = String(nextValue) as ContactWorkbenchTaskPoolType;
                setTaskPoolType(nextTaskType);
                setTaskPoolName(
                  createDefaultTaskPoolName(
                    nextTaskType,
                    selectedContactCount,
                    currentOperatorName,
                  ),
                );
              }}
            >
              <Radio value="tele_sales">电销任务池</Radio>
              <Radio value="strong_follow">强跟任务池</Radio>
            </Radio.Group>
          </div>
          <label className="crm-contact-workbench-field">
            <span>任务名称</span>
            <Input value={taskPoolName} onChange={setTaskPoolName} />
          </label>
        </div>
      </Modal>

      <Modal
        title="修改临时任务"
        visible={Boolean(temporaryTaskEditContext)}
        unmountOnExit
        footer={
          <Space>
            <Button
              onClick={() => {
                setTemporaryTaskEditContext(null);
                setTargetTemporaryTaskUserId(undefined);
              }}
            >
              取消
            </Button>
            <Button
              type="primary"
              loading={runtime.mutating}
              disabled={!targetTemporaryTaskUserId}
              onClick={handleConfirmTemporaryTaskHandler}
            >
              确定
            </Button>
          </Space>
        }
        onCancel={() => {
          setTemporaryTaskEditContext(null);
          setTargetTemporaryTaskUserId(undefined);
        }}
      >
        <div className="crm-contact-workbench-transfer-form">
          <p>
            {temporaryTaskEditContext
              ? `正在设置「${temporaryTaskEditContext.contactName}」的临时任务处理人`
              : ""}
          </p>
          <p className="crm-contact-workbench-modal-hint">
            {temporaryTaskEditContext?.assignment.taskName}
          </p>
          <Button
            long
            type="outline"
            status="danger"
            loading={runtime.mutating}
            onClick={() => void handleClearAndWithdrawTemporaryTask()}
          >
            清空处理人并收回
          </Button>
          <div data-testid="contact-temporary-task-handler-select">
            <StaffSelect
              placeholder="选择临时任务处理人"
              value={targetTemporaryTaskUserId}
              options={runtime.staffOptions}
              onChange={setTargetTemporaryTaskUserId}
            />
          </div>
        </div>
      </Modal>

      <Modal
        title="粘贴学校名称"
        visible={schoolNamePasteModalVisible}
        confirmLoading={schoolNamePasteApplying}
        okText="识别并选中"
        cancelText="取消"
        onCancel={() => {
          if (schoolNamePasteApplying) {
            return;
          }
          setSchoolNamePasteModalVisible(false);
        }}
        onOk={() => void applyPastedSchoolNames(splitPastedSchoolNames(schoolNamePasteText))}
      >
        <p className="crm-contact-workbench-modal-hint">
          支持换行、逗号、顿号或分号分隔；按校名精确匹配后写入学校筛。也可在学校框内直接粘贴多行校名。
        </p>
        <Input.TextArea
          data-testid="contact-school-paste-textarea"
          autoFocus
          value={schoolNamePasteText}
          placeholder={"例如：\n河北师范大学\n华北理工大学\n邢台学院"}
          style={{ minHeight: 160 }}
          onChange={setSchoolNamePasteText}
        />
      </Modal>

      <Modal
        title="粘贴学校名称"
        visible={schoolBatchPasteModalVisible}
        confirmLoading={schoolBatchPasteApplying}
        okText="识别并加入"
        cancelText="取消"
        onCancel={() => {
          if (schoolBatchPasteApplying) {
            return;
          }
          setSchoolBatchPasteModalVisible(false);
        }}
        onOk={() =>
          void applyPastedSchoolNamesToBatchAssignment(splitPastedSchoolNames(schoolBatchPasteText))
        }
      >
        <p className="crm-contact-workbench-modal-hint">
          支持换行、逗号、顿号或分号分隔；匹配启用学校并加入已选清单。也可在「搜索补充学校」框内直接粘贴多行校名。
        </p>
        <Input.TextArea
          data-testid="school-batch-paste-textarea"
          autoFocus
          value={schoolBatchPasteText}
          placeholder={"例如：\n河北师范大学\n华北理工大学\n邢台学院"}
          style={{ minHeight: 160 }}
          onChange={setSchoolBatchPasteText}
        />
      </Modal>

      <Drawer
        className="crm-contact-workbench-school-batch-drawer"
        title="按学校批量分配联系人"
        visible={schoolBatchAssignmentVisible}
        width={680}
        footer={
          <div className="crm-contact-workbench-school-batch-footer">
            <Typography.Text type="secondary">
              已选 {schoolBatchAssignmentSchoolIds.length} 所学校，涉及 {schoolBatchAssignmentContactCount} 位联系人
            </Typography.Text>
            <Space>
              <Button onClick={handleCloseSchoolBatchAssignment}>取消</Button>
              <Button
                type="primary"
                loading={runtime.schoolBatchAssignmentConfirming}
                disabled={
                  schoolBatchAssignmentSchoolIds.length === 0 ||
                  schoolBatchAssignmentContactCount === 0 ||
                  (schoolBatchAssignmentMode === "join_task_pool"
                    ? !schoolBatchAssignmentTaskName.trim()
                    : schoolBatchAssignmentTargetUserIds.length === 0)
                }
                onClick={handleSubmitSchoolBatchAssignment}
              >
                确定
              </Button>
            </Space>
          </div>
        }
        onCancel={handleCloseSchoolBatchAssignment}
      >
        <div className="crm-contact-workbench-school-batch-form">
          <section className="crm-contact-workbench-school-batch-region">
            <div className="crm-contact-workbench-school-batch-section-heading">
              <strong>按省市批量加入</strong>
              <Typography.Text type="secondary">省份与城市按并集匹配学校</Typography.Text>
            </div>
            <div className="crm-contact-workbench-school-batch-region-fields">
              <label className="crm-contact-workbench-field">
                <span>省份</span>
                <div data-testid="school-batch-assignment-province-select">
                  <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder="选择一个或多个省份"
                    value={schoolBatchAssignmentSelectedProvinces}
                    loading={schoolBatchAssignmentScopeLoading}
                    maxTagCount={2}
                    onChange={(nextValue) =>
                      setSchoolBatchAssignmentSelectedProvinces(
                        Array.isArray(nextValue) ? nextValue.map(String) : [],
                      )
                    }
                  >
                    {schoolBatchAssignmentRegionOptions.provinces.map((province) => (
                      <Select.Option key={province} value={province}>
                        {province}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </label>
              <label className="crm-contact-workbench-field">
                <span>城市</span>
                <div data-testid="school-batch-assignment-city-select">
                  <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder="选择一个或多个城市"
                    value={schoolBatchAssignmentSelectedCityKeys}
                    loading={schoolBatchAssignmentScopeLoading}
                    maxTagCount={2}
                    onChange={(nextValue) =>
                      setSchoolBatchAssignmentSelectedCityKeys(
                        Array.isArray(nextValue) ? nextValue.map(String) : [],
                      )
                    }
                  >
                    {schoolBatchAssignmentRegionOptions.cities.map((city) => (
                      <Select.Option key={city.value} value={city.value}>
                        {city.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </label>
            </div>
            <Button
              long
              type="outline"
              disabled={schoolBatchAssignmentPendingRegionSchools.length === 0}
              loading={schoolBatchAssignmentScopeLoading}
              onClick={handleAddSchoolBatchAssignmentRegionSchools}
            >
              {schoolBatchAssignmentPendingRegionSchools.length > 0
                ? `加入 ${schoolBatchAssignmentPendingRegionSchools.length} 所学校`
                : schoolBatchAssignmentMatchedRegionSchools.length > 0
                  ? "所选地区学校已全部加入"
                  : "请选择省份或城市"}
            </Button>
          </section>
          <label className="crm-contact-workbench-field">
            <span className="crm-contact-workbench-school-filter-label">
              搜索补充学校
              <Button
                type="text"
                size="mini"
                className="crm-contact-workbench-school-paste-trigger"
                data-testid="school-batch-paste-trigger"
                onClick={() => {
                  setSchoolBatchPasteText("");
                  setSchoolBatchPasteModalVisible(true);
                }}
              >
                粘贴校名
              </Button>
            </span>
            <div data-testid="school-batch-assignment-select" onPaste={handleSchoolBatchAssignmentPaste}>
              <Select
                mode="multiple"
                showSearch
                placeholder="搜索并选择学校"
                value={[]}
                inputValue={schoolBatchAssignmentSearchKeyword}
                loading={schoolBatchAssignmentScopeLoading || schoolBatchPasteApplying}
                filterOption={(inputValue, option) =>
                  String(option?.props?.searchText ?? "")
                    .toLowerCase()
                    .includes(inputValue.trim().toLowerCase())
                }
                onInputValueChange={(value, reason) => {
                  // 勾选/收起弹层时 Arco 会清空搜索词；只接受用户手动输入，便于连续多选。
                  if (reason === "manual") {
                    setSchoolBatchAssignmentSearchKeyword(value);
                  }
                }}
                onChange={(nextValue) =>
                  handleSchoolBatchAssignmentSearchPick(
                    Array.isArray(nextValue) ? nextValue : [],
                  )
                }
                dropdownMenuStyle={{ maxHeight: 320 }}
              >
                {schoolBatchAssignmentSelectOptions.map((option) => (
                  <Select.Option
                    key={option.id}
                    value={option.id}
                    disabled={schoolBatchAssignmentSchoolIds.includes(option.id)}
                    searchText={`${option.orgName} ${option.province ?? ""} ${option.city ?? ""}`}
                  >
                    {option.orgName} · {option.province ?? ""} / {option.city ?? ""}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </label>
          <section
            className="crm-contact-workbench-school-batch-selected"
            aria-label="已选学校清单"
          >
            <div className="crm-contact-workbench-school-batch-section-heading">
              <strong>已选学校（{schoolBatchAssignmentSchoolIds.length}）</strong>
              <Typography.Text type="secondary">可移除个别学校后再执行分配</Typography.Text>
            </div>
            {schoolBatchAssignmentSelectedSchools.length > 0 ? (
              <>
                <Input
                  allowClear
                  data-testid="school-batch-selected-name-filter"
                  placeholder="按学校名称模糊筛选已选清单"
                  value={schoolBatchAssignmentSelectedNameFilter}
                  onChange={setSchoolBatchAssignmentSelectedNameFilter}
                />
                {schoolBatchAssignmentVisibleSelectedSchools.length > 0 ? (
                  <div className="crm-contact-workbench-school-batch-selected-list" role="list">
                    {schoolBatchAssignmentVisibleSelectedSchools.map((school) => (
                      <div
                        className="crm-contact-workbench-school-batch-selected-item"
                        key={school.id}
                        role="listitem"
                      >
                        <div>
                          <strong>{school.orgName}</strong>
                          <Typography.Text type="secondary">
                            {school.province ?? "省份未填写"} / {school.city ?? "城市未填写"}
                          </Typography.Text>
                        </div>
                        <Button
                          type="text"
                          size="mini"
                          status="danger"
                          aria-label={`移除 ${school.orgName}`}
                          icon={<Trash2 size={14} />}
                          onClick={() =>
                            void handleSchoolBatchAssignmentSchoolsChange(
                              schoolBatchAssignmentSchoolIds.filter((id) => id !== school.id),
                            )
                          }
                        >
                          移除
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Typography.Text type="secondary">没有匹配的已选学校</Typography.Text>
                )}
              </>
            ) : (
              <Typography.Text type="secondary">暂未选择学校</Typography.Text>
            )}
          </section>
          <div className="crm-contact-workbench-school-batch-mode">
            <span className="crm-contact-workbench-field-label">分配方式</span>
            <Radio.Group
              type="button"
              value={schoolBatchAssignmentMode}
              onChange={(value) => {
                const nextMode = value as ContactWorkbenchSchoolBatchAssignmentMode;
                setSchoolBatchAssignmentMode(nextMode);
                setSchoolBatchAssignmentTargetUserIds([]);
                if (nextMode === "join_task_pool") {
                  setSchoolBatchAssignmentTaskName(
                    createDefaultTaskPoolName(
                      "tele_sales",
                      schoolBatchAssignmentContactCount,
                      currentOperatorName,
                    ),
                  );
                }
              }}
            >
              <Radio value="join_task_pool">加入电销任务池</Radio>
              <Radio value="transfer_primary">转移主销售</Radio>
              <Radio value="add_shared">添加共享销售</Radio>
            </Radio.Group>
          </div>
          {schoolBatchAssignmentMode === "join_task_pool" ? (
            <label className="crm-contact-workbench-field" data-testid="school-batch-assignment-task-name-field">
              <span>任务名称</span>
              <Input
                data-testid="school-batch-assignment-task-name"
                value={schoolBatchAssignmentTaskName}
                maxLength={300}
                placeholder="请输入电销任务名称"
                onChange={setSchoolBatchAssignmentTaskName}
              />
            </label>
          ) : schoolBatchAssignmentMode === "transfer_primary" ? (
            <label className="crm-contact-workbench-field" data-testid="school-batch-assignment-primary-sale">
              <span>目标主销售</span>
              <StaffSelect
                placeholder="选择目标主销售"
                value={schoolBatchAssignmentTargetUserIds[0]}
                options={runtime.staffOptions}
                dropdownMenuStyle={{ maxHeight: 320 }}
                onChange={(value) => setSchoolBatchAssignmentTargetUserIds(value ? [value] : [])}
              />
            </label>
          ) : (
            <div className="crm-contact-workbench-field" data-testid="school-batch-assignment-shared-sales">
              <span>添加共享销售</span>
              <StaffMultiSelect
                placeholder="选择共享销售，可多选"
                value={schoolBatchAssignmentTargetUserIds}
                options={runtime.staffOptions}
                dropdownMenuStyle={{ maxHeight: 320 }}
                onChange={setSchoolBatchAssignmentTargetUserIds}
              />
            </div>
          )}
          <Typography.Text type="secondary">
            {schoolBatchAssignmentMode === "join_task_pool"
              ? "可选择任意启用学校；确定后仅分配你作为主销售或共享销售的联系人。加入电销任务池默认投放电销池，可修改任务名称。"
              : "可选择任意启用学校；确定后仅分配你作为主销售或共享销售的联系人。转移主销售会保留原有共享销售，添加共享销售不会改变主销售。"}
          </Typography.Text>
        </div>
      </Drawer>

      <Drawer
        className={`crm-contact-workbench-detail-drawer${isMobileViewport ? " is-mobile" : ""}${
          isMobileViewport ? ` is-mobile-${mobileContactDrawerMode}` : ""
        }`}
        title={
          isMobileViewport
            ? mobileContactDrawerMode === "record"
              ? "新增动态"
              : mobileContactDrawerMode === "records"
                ? "动态记录"
                : "修改联系人"
            : (runtime.selectedContactDetail?.contactName ?? "联系人详情")
        }
        visible={Boolean(runtime.selectedContactId)}
        width={isMobileViewport ? "100%" : 1420}
        height={isMobileViewport ? "92vh" : undefined}
        placement={isMobileViewport ? "bottom" : "right"}
        footer={null}
        autoFocus={false}
        onCancel={() => {
          runtime.handleCloseContact();
          setRecordDraft(createDefaultContactRecordDraft());
          setMobileContactDrawerMode("edit");
        }}
      >
        {runtime.detailLoading ? (
          <div className="crm-contact-workbench-detail-loading">
            <Spin />
          </div>
        ) : isMobileViewport && mobileContactDrawerMode === "record" ? (
          <div className="crm-contact-workbench-detail-mobile-pane">
            <ContactRecordCreator
              key={`${runtime.selectedContactId}:${recordDraft.recordTypeCode}`}
              draft={recordDraft}
              recordTypeOptions={runtime.metadata?.recordTypeOptions ?? []}
              recordLabelGroups={runtime.metadata?.recordLabelGroups ?? []}
              mutating={runtime.recordMutating}
              canRecord={canRecordSelectedContact}
              actionPlacement="footer"
              onDraftChange={setRecordDraft}
              onSubmit={() => void handleCreateRecord()}
            />
          </div>
        ) : isMobileViewport && mobileContactDrawerMode === "records" ? (
          <div className="crm-contact-workbench-detail-mobile-pane">
            <ContactRecordList
              records={runtime.selectedContactRecords}
              mutating={runtime.recordMutating}
              canDeleteRecord={canDeleteSelectedContactRecord}
              onDelete={(record) => void handleDeleteRecord(record)}
            />
          </div>
        ) : isMobileViewport ? (
          <div className="crm-contact-workbench-detail-mobile-pane">
            <ContactEditor
              draft={
                contactDraft ??
                (runtime.selectedContactDetail
                  ? createContactDraft(runtime.selectedContactDetail)
                  : null)
              }
              detail={runtime.selectedContactDetail}
              contactStarOptions={runtime.metadata?.contactStarOptions ?? []}
              mutating={runtime.contactMutating}
              canEdit={canEditSelectedContact}
              actionPlacement="footer"
              onSearchOrganizations={runtime.searchOrganizations}
              onSearchOrganizationUnits={runtime.searchOrganizationUnits}
              onDraftChange={setContactDraft}
              onSave={() => void handleSaveContact()}
            />
          </div>
        ) : (
          <div className="crm-contact-workbench-detail-grid">
            <ContactEditor
              draft={
                contactDraft ??
                (runtime.selectedContactDetail
                  ? createContactDraft(runtime.selectedContactDetail)
                  : null)
              }
              detail={runtime.selectedContactDetail}
              contactStarOptions={runtime.metadata?.contactStarOptions ?? []}
              mutating={runtime.contactMutating}
              canEdit={canEditSelectedContact}
              onSearchOrganizations={runtime.searchOrganizations}
              onSearchOrganizationUnits={runtime.searchOrganizationUnits}
              onDraftChange={setContactDraft}
              onSave={() => void handleSaveContact()}
            />
            <ContactRecordList
              records={runtime.selectedContactRecords}
              mutating={runtime.recordMutating}
              canDeleteRecord={canDeleteSelectedContactRecord}
              onDelete={(record) => void handleDeleteRecord(record)}
            />
            <ContactRecordCreator
              key={`${runtime.selectedContactId}:${recordDraft.recordTypeCode}`}
              draft={recordDraft}
              recordTypeOptions={runtime.metadata?.recordTypeOptions ?? []}
              recordLabelGroups={runtime.metadata?.recordLabelGroups ?? []}
              mutating={runtime.recordMutating}
              canRecord={canRecordSelectedContact}
              onDraftChange={setRecordDraft}
              onSubmit={() => void handleCreateRecord()}
            />
          </div>
        )}
      </Drawer>

      <QuickOpportunityDrawer
        visible={Boolean(runtime.quickOpportunityContactId)}
        check={runtime.quickOpportunityCheck}
        loading={runtime.quickOpportunityLoading}
        creatingMonth={runtime.quickOpportunityCreatingMonth}
        onCancel={runtime.handleCloseQuickOpportunity}
        onCreate={(travelMonth) => void runtime.handleCreateQuickOpportunity(travelMonth)}
      />

      <Drawer
        className={`crm-contact-workbench-create-drawer${isMobileViewport ? " is-mobile" : ""}`}
        title="新增联系人"
        visible={Boolean(createDraft)}
        width={isMobileViewport ? "100%" : 560}
        height={isMobileViewport ? "92vh" : undefined}
        placement={isMobileViewport ? "bottom" : "right"}
        footer={null}
        onCancel={() => setCreateDraft(null)}
      >
        {createDraft ? (
          <div data-testid="contact-create-drawer">
            <ContactEditor
              draft={createDraft}
              contactStarOptions={runtime.metadata?.contactStarOptions ?? []}
              mutating={runtime.contactMutating}
              canEdit
              onSearchOrganizations={runtime.searchOrganizations}
              onSearchOrganizationUnits={runtime.searchOrganizationUnits}
              onDraftChange={setCreateDraft}
              onSave={() => void handleCreateContact()}
            />
          </div>
        ) : null}
      </Drawer>

      <Modal
        title="移除共享销售"
        visible={Boolean(sharedOwnerDeleteConfirm)}
        confirmLoading={runtime.mutating}
        okText="确认移除"
        cancelText="取消"
        onOk={handleConfirmRemoveSharedOwner}
        onCancel={() => setSharedOwnerDeleteConfirm(null)}
      >
        {sharedOwnerDeleteConfirm
          ? `确认把「${sharedOwnerDeleteConfirm.userName}」从该联系人共享销售中移除吗？`
          : null}
      </Modal>
    </main>
  );
}

/** 自然语言查询解析摘要。 */
function NlQuerySummary({ result }: { result: ContactWorkbenchNlQueryParseResult | null }) {
  if (!result) {
    return null;
  }
  if (!result.executable) {
    return (
      <Alert
        className="crm-contact-workbench-nl-result"
        type="warning"
        showIcon
        content={result.messages.join("；") || "智能查询未识别出可执行筛选条件。"}
      />
    );
  }
  return (
    <div className="crm-contact-workbench-nl-result" aria-label="智能查询识别结果">
      <Typography.Text type="secondary">已识别</Typography.Text>
      <Space size={[6, 6]} wrap>
        {result.summary.map((item) => (
          <Tag color="arcoblue" key={item}>
            {item}
          </Tag>
        ))}
      </Space>
    </div>
  );
}

/** 构建联系人工作台表格列。 */
function buildContactColumns({
  visibleColumns,
  latestRecordLabels,
  latestRecordLabelsLoading,
  giftCountsLoading,
  permissionUser,
  sortField,
  sortDirection,
  onSort,
  onOpenContact,
  onOpenQuickOpportunity,
  onOpenGifts,
  onOpenSharedOwnerEditor,
  onRemoveSharedOwner,
  onEditTemporaryTask,
  onWithdrawTemporaryTask,
  ownerFilterOptions,
  ownerFilterLoading,
  ownerUserIds,
  sharedOwnerUserIds,
  temporaryHandlerUserIds,
  onApplyOwnerFilters,
}: {
  visibleColumns: ContactWorkbenchColumnKey[];
  latestRecordLabels: Record<number, string>;
  latestRecordLabelsLoading: boolean;
  giftCountsLoading: boolean;
  permissionUser: PermissionUser | null | undefined;
  sortField?: string;
  sortDirection: SortDirection;
  onSort: (field: string, direction: SortDirection) => void;
  onOpenContact: (contactId: number) => void;
  onOpenQuickOpportunity: (contactId: number) => void;
  onOpenGifts: (contactId: number) => void;
  onOpenSharedOwnerEditor: (item: ContactWorkbenchItem) => void;
  onRemoveSharedOwner: (item: ContactWorkbenchItem, userId: number, userName: string) => void;
  onEditTemporaryTask: (
    item: ContactWorkbenchItem,
    assignment: ContactWorkbenchTemporaryTaskAssignment,
  ) => void;
  onWithdrawTemporaryTask: (
    item: ContactWorkbenchItem,
    assignment: ContactWorkbenchTemporaryTaskAssignment,
  ) => void;
  ownerFilterOptions?: ContactWorkbenchOwnerOptions;
  ownerFilterLoading?: boolean;
  ownerUserIds: number[];
  sharedOwnerUserIds: number[];
  temporaryHandlerUserIds: number[];
  onApplyOwnerFilters: (next: {
    ownerUserIds: number[];
    sharedOwnerUserIds: number[];
    temporaryHandlerUserIds: number[];
  }) => void;
}): TableColumnProps<ContactWorkbenchItem>[] {
  const visibleSet = new Set(visibleColumns);
  const columnByKey = new Map<
    ContactWorkbenchColumnKey,
    TableColumnProps<ContactWorkbenchItem>
  >();

  if (visibleSet.has("contact")) {
    columnByKey.set("contact", {
      title: (
        <SortableHeader
          label="联系人"
          field="contact_name"
          nowrap
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      dataIndex: "contact_name",
      field: "contact_name",
      fixed: "left",
      width: 120,
      render: (_, item) => {
        const positionText = item.positionName?.trim() || "";
        return (
          <div className="crm-contact-workbench-main-cell">
            <EllipsisHover content={item.contactName}>
              {item.canOpenContact !== false ? (
                <button
                  type="button"
                  className="crm-contact-workbench-contact-link"
                  onClick={() => onOpenContact(item.id)}
                >
                  {item.contactName}
                </button>
              ) : (
                <span className="crm-contact-workbench-contact-text">{item.contactName}</span>
              )}
            </EllipsisHover>
            {positionText ? (
              <EllipsisHover content={positionText}>
                <span>{positionText}</span>
              </EllipsisHover>
            ) : null}
          </div>
        );
      },
    });
  }
  if (visibleSet.has("org")) {
    columnByKey.set("org", {
      title: (
        <SortableHeader
          label="学校/院系"
          field="org_name"
          nowrap
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      dataIndex: "org_name",
      field: "org_name",
      width: 200,
      render: (_, item) => {
        const unitPath = item.mainOrgUnitPathName || item.mainOrgUnitName || "院系待补";
        return (
          <div className="crm-contact-workbench-main-cell">
            <EllipsisHover content={item.orgName}>
              <strong>{item.orgName}</strong>
            </EllipsisHover>
            <EllipsisHover content={unitPath}>
              <span className="crm-contact-workbench-org-unit-path">{unitPath}</span>
            </EllipsisHover>
          </div>
        );
      },
    });
  }
  if (visibleSet.has("owners")) {
    columnByKey.set("owners", {
      title: (
        <ContactOwnerFilterDropdown
          options={ownerFilterOptions}
          loading={ownerFilterLoading}
          ownerUserIds={ownerUserIds}
          sharedOwnerUserIds={sharedOwnerUserIds}
          temporaryHandlerUserIds={temporaryHandlerUserIds}
          onApply={onApplyOwnerFilters}
        />
      ),
      // 需容纳主销售名 +「待认领」图标按钮，过窄会把三字标签拆成两行。
      width: 120,
      render: (_, item) => (
        <OwnersCell
          item={item}
          permissionUser={permissionUser}
          onEditSharedOwners={onOpenSharedOwnerEditor}
          onRemoveSharedOwner={onRemoveSharedOwner}
          onEditTemporaryTask={onEditTemporaryTask}
          onWithdrawTemporaryTask={onWithdrawTemporaryTask}
        />
      ),
    });
  }
  if (visibleSet.has("phone")) {
    columnByKey.set("phone", {
      title: "手机/微信",
      width: 130,
      render: (_, item) => <PhoneWechatPopover item={item} />,
    });
  }
  if (visibleSet.has("area")) {
    columnByKey.set("area", {
      title: "省/市区",
      width: 180,
      render: (_, item) => {
        const areaText = formatContactArea(item.province, item.city, item.district);
        return (
          <EllipsisHover content={areaText}>
            <span className="crm-contact-workbench-plain-ellipsis">{areaText}</span>
          </EllipsisHover>
        );
      },
    });
  }
  if (visibleSet.has("recordLabel")) {
    columnByKey.set("recordLabel", {
      title: "动态标签",
      width: 140,
      render: (_, item) => {
        const labelText = latestRecordLabels[item.id] ?? (latestRecordLabelsLoading ? "加载中…" : "-");
        const labels = splitContactRecordLabels(
          labelText === "-" || labelText === "加载中…" ? undefined : labelText,
        );
        const maxInlineLabels = 2;
        const visibleLabels = labels.slice(0, maxInlineLabels);
        const hasOverflowLabels = labels.length > maxInlineLabels;
        const canOpenQuickOpportunity =
          labels.length > 0 &&
          item.canOpenContact !== false &&
          canUsePermission(permissionUser, "crm.contact:access") &&
          canAccessCrmOperation(
            permissionUser,
            "crm.opportunity:access",
            item.accessRole === "region_manager" || item.accessRole === "primary_owner",
          );
        const labelContent =
          labels.length > 0 ? (
            <span className="crm-contact-workbench-record-label-tags">
              {visibleLabels.map((label) => (
                <Tag key={label} color={resolveContactRecordLabelColor(label)} size="small">
                  {label}
                </Tag>
              ))}
              {hasOverflowLabels ? (
                <Tag size="small" color="gray" className="crm-contact-workbench-record-label-ellipsis">
                  …
                </Tag>
              ) : null}
            </span>
          ) : (
            labelText
          );
        return (
          <Popover
            position="top"
            trigger="hover"
            content={
              <div className="crm-contact-workbench-record-label-popover">
                {labels.length > 0 ? (
                  <span className="crm-contact-workbench-record-label-tags">
                    {labels.map((label) => (
                      <Tag key={label} color={resolveContactRecordLabelColor(label)} size="small">
                        {label}
                      </Tag>
                    ))}
                  </span>
                ) : (
                  labelText
                )}
              </div>
            }
          >
            {canOpenQuickOpportunity ? (
              <button
                type="button"
                className="crm-contact-workbench-record-label-cell crm-contact-workbench-record-label-button"
                aria-label={`根据${item.contactName}的动态标签快捷新增商机`}
                onClick={() => onOpenQuickOpportunity(item.id)}
              >
                {labelContent}
              </button>
            ) : (
              <span className="crm-contact-workbench-record-label-cell">{labelContent}</span>
            )}
          </Popover>
        );
      },
    });
  }
  if (visibleSet.has("record")) {
    columnByKey.set("record", {
      title: (
        <SortableHeader
          label="动态"
          field="record_count"
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      dataIndex: "record_count",
      field: "record_count",
      width: 130,
      render: (_, item) => (
        <Space size={6}>
          {item.canOpenContact !== false ? (
            <button
              type="button"
              className="crm-contact-workbench-record-count-button"
              onClick={() => onOpenContact(item.id)}
            >
              <Tag color="arcoblue">{item.recordCount} 条</Tag>
            </button>
          ) : (
            <Tag color="arcoblue">{item.recordCount} 条</Tag>
          )}
          {item.attachmentCount > 0 ? <Tag color="green">附件 {item.attachmentCount}</Tag> : null}
        </Space>
      ),
    });
  }
  if (visibleSet.has("latestRecord")) {
    columnByKey.set("latestRecord", {
      title: (
        <SortableHeader
          label="最近动态"
          field="last_contact_at"
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      dataIndex: "last_contact_at",
      field: "last_contact_at",
      width: 300,
      render: (_, item) => <LatestRecordPopover item={item} />,
    });
  }
  if (visibleSet.has("status")) {
    columnByKey.set("status", {
      title: "状态",
      width: 100,
      render: (_, item) => (
        <Space size={6}>
          <Tag color={item.isActive ? "green" : "gray"}>{item.isActiveLabel}</Tag>
          {item.isKeyContact ? <Tag color="orange">{item.isKeyContactLabel}</Tag> : null}
        </Space>
      ),
    });
  }
  if (visibleSet.has("star")) {
    columnByKey.set("star", {
      title: "星级",
      width: 118,
      render: (_, item) => (
        <Tag color={item.contactStar ? "gold" : "gray"}>
          <span className="crm-contact-workbench-star">
            <Star size={14} fill={item.contactStar ? "currentColor" : "none"} />
            {formatContactStar(item.contactStar)}
          </span>
        </Tag>
      ),
    });
  }
  if (visibleSet.has("gift")) {
    columnByKey.set("gift", {
      title: (
        <SortableHeader
          label="礼品"
          field="gift_count"
          nowrap
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      dataIndex: "gift_count",
      field: "gift_count",
      width: 104,
      render: (_, item) => {
        const giftCountText = giftCountsLoading ? "加载中…" : `${item.giftCount} 条`;
        const canOpenGift = canAccessCrmOperation(
          permissionUser,
          "crm.contact:gift",
          item.canOpenContact !== false,
        );
        return canOpenGift ? (
          <button
            type="button"
            className="crm-contact-workbench-record-count-button"
            aria-label={
              giftCountsLoading
                ? `${item.contactName}的礼品数量加载中`
                : `查看${item.contactName}的礼品记录，共 ${item.giftCount} 条`
            }
            onClick={() => onOpenGifts(item.id)}
          >
            <Tag color="purple">
              <Gift size={13} /> {giftCountText}
            </Tag>
          </button>
        ) : (
          <Tag color="purple">{giftCountText}</Tag>
        );
      },
    });
  }
  if (visibleSet.has("createdAt")) {
    columnByKey.set("createdAt", {
      title: (
        <SortableHeader
          label="新增时间"
          field="created_at"
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      dataIndex: "created_at",
      field: "created_at",
      width: 150,
      render: (_, item) => {
        const createdAtText = formatContactWorkbenchHour(item.createdAt);
        return (
          <EllipsisHover content={createdAtText}>
            <span className="crm-contact-workbench-plain-ellipsis">{createdAtText}</span>
          </EllipsisHover>
        );
      },
    });
  }
  if (visibleSet.has("updatedAt")) {
    columnByKey.set("updatedAt", {
      title: (
        <SortableHeader
          label="更新时间"
          field="updated_at"
          activeField={sortField}
          activeDirection={sortDirection}
          onSort={onSort}
        />
      ),
      dataIndex: "updated_at",
      field: "updated_at",
      width: 170,
      render: (_, item) => {
        const updatedAtText = formatContactWorkbenchHour(item.updatedAt);
        return (
          <EllipsisHover content={updatedAtText}>
            <span className="crm-contact-workbench-plain-ellipsis">{updatedAtText}</span>
          </EllipsisHover>
        );
      },
    });
  }
  return visibleColumns
    .map((key) => columnByKey.get(key))
    .filter((column): column is TableColumnProps<ContactWorkbenchItem> => Boolean(column));
}

/** 列表人数展示，大数字千分位分隔。 */
function formatContactCount(value: number) {
  return value.toLocaleString("zh-CN");
}

/** 列表截断文本悬停展示全文。 */
function EllipsisHover({
  content,
  children,
  className = "crm-contact-workbench-ellipsis-trigger",
}: {
  content?: string | null;
  children: ReactNode;
  className?: string;
}) {
  const text = (content ?? "").trim();
  const trigger = <span className={className}>{children}</span>;
  if (!text) {
    return trigger;
  }
  return (
    <Tooltip content={text} position="top" mouseEnterDelay={0}>
      {trigger}
    </Tooltip>
  );
}

/** 紧凑展示主销售，并用弹层查看和维护共享销售。 */
function OwnersCell({
  item,
  permissionUser,
  onEditSharedOwners,
  onRemoveSharedOwner,
  onEditTemporaryTask,
  onWithdrawTemporaryTask,
}: {
  item: ContactWorkbenchItem;
  permissionUser: PermissionUser | null | undefined;
  onEditSharedOwners: (item: ContactWorkbenchItem) => void;
  onRemoveSharedOwner: (item: ContactWorkbenchItem, userId: number, userName: string) => void;
  onEditTemporaryTask: (
    item: ContactWorkbenchItem,
    assignment: ContactWorkbenchTemporaryTaskAssignment,
  ) => void;
  onWithdrawTemporaryTask: (
    item: ContactWorkbenchItem,
    assignment: ContactWorkbenchTemporaryTaskAssignment,
  ) => void;
}) {
  const canManageOwners = canAccessCrmOperation(
    permissionUser,
    "crm.contact:assign",
    item.canAssign,
  );
  const sharedOwners = (item.sharedOwnerUserIds ?? [])
    .map((userId, index) => ({
      userId,
      name: item.sharedOwnerUserNames?.[index] ?? `员工 ${userId}`,
    }))
    .filter((owner) => Boolean(owner.name));
  const temporaryTasks = item.temporaryTaskAssignments ?? [];
  const temporaryHandlerCount = new Set(
    temporaryTasks.flatMap((assignment) =>
      assignment.handlerUserId ? [assignment.handlerUserId] : [],
    ),
  ).size;
  const temporaryTaskLabel =
    temporaryHandlerCount > 0
      ? `临时任务 ${temporaryHandlerCount} 人`
      : "待认领";
  const ownerText = formatContactOwners(item.ownerUserNames);
  return (
    <div className="crm-contact-workbench-owner-cell">
      <EllipsisHover content={ownerText}>
        <span>{ownerText}</span>
      </EllipsisHover>
      {sharedOwners.length > 0 ? (
        <Popover
          position="left"
          trigger="hover"
          content={
            <div className="crm-contact-workbench-shared-owner-popover">
              <div className="crm-contact-workbench-shared-owner-popover-header">
                <strong>共享销售</strong>
                <Button size="mini" type="text" disabled={!canManageOwners} onClick={() => onEditSharedOwners(item)}>
                  修改
                </Button>
              </div>
              <div>
                {sharedOwners.map((owner) => (
                  <Tag key={`${owner.userId}-${owner.name}`} color="arcoblue">
                    <span>{owner.name}</span>
                    <Button
                      size="mini"
                      type="text"
                      className="crm-contact-workbench-shared-owner-remove"
                      icon={<Trash2 size={12} />}
                      aria-label={`移除共享销售 ${owner.name}`}
                      disabled={!canManageOwners}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onRemoveSharedOwner(item, owner.userId, owner.name);
                      }}
                    />
                  </Tag>
                ))}
              </div>
            </div>
          }
        >
          <button
            type="button"
            className="crm-contact-workbench-shared-owner-button"
            disabled={!canManageOwners}
            onClick={() => onEditSharedOwners(item)}
          >
            共享 {sharedOwners.length} 人
          </button>
        </Popover>
      ) : null}
      {temporaryTasks.length > 0 ? (
        <Popover
          position="left"
          trigger="hover"
          content={
            <div className="crm-contact-workbench-temporary-task-popover">
              <strong>临时任务</strong>
              {temporaryTasks.map((assignment) => (
                <div key={assignment.itemId} className="crm-contact-workbench-temporary-task-row">
                  <div>
                    <span>{assignment.taskName}</span>
                    <strong>{assignment.handlerUserName || "待认领"}</strong>
                  </div>
                  {canManageOwners ? (
                    <div className="crm-contact-workbench-temporary-task-actions">
                      <Button
                        size="mini"
                        type="text"
                        icon={<Clock3 size={13} />}
                        aria-label={`修改临时任务 ${assignment.taskName}`}
                        onClick={() => onEditTemporaryTask(item, assignment)}
                      >
                        修改
                      </Button>
                      {assignment.accessStatus === "available" && !assignment.handlerUserId ? (
                        <Button
                          size="mini"
                          type="text"
                          status="danger"
                          icon={<RotateCcw size={13} />}
                          aria-label={`收回临时任务 ${assignment.taskName}`}
                          onClick={() => onWithdrawTemporaryTask(item, assignment)}
                        >
                          收回
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          }
        >
          <button
            type="button"
            className="crm-contact-workbench-temporary-task-button"
            aria-label={temporaryTaskLabel}
            disabled={!canManageOwners}
            onClick={() => {
              if (canManageOwners && temporaryTasks.length === 1) {
                onEditTemporaryTask(item, temporaryTasks[0]);
              }
            }}
          >
            <Clock3 size={12} />
            {temporaryTaskLabel}
          </button>
        </Popover>
      ) : null}
    </div>
  );
}

/** 可直接指定升序或降序的表头。 */
function SortableHeader({
  label,
  field,
  nowrap = false,
  activeField,
  activeDirection,
  onSort,
}: {
  label: string;
  field: string;
  nowrap?: boolean;
  activeField?: string;
  activeDirection: SortDirection;
  onSort: (field: string, direction: SortDirection) => void;
}) {
  const active = activeField === field;
  return (
    <div
      className={`crm-contact-workbench-sort-header${
        nowrap ? " crm-contact-workbench-sort-header--nowrap" : ""
      }`}
    >
      <span>{label}</span>
      <span className="crm-contact-workbench-sort-actions">
        <Button
          size="mini"
          shape="circle"
          aria-label={`${label}升序`}
          type={active && activeDirection === "asc" ? "primary" : "secondary"}
          icon={<ArrowUp size={12} />}
          onClick={(event) => {
            event.stopPropagation();
            onSort(field, "asc");
          }}
        />
        <Button
          size="mini"
          shape="circle"
          aria-label={`${label}降序`}
          type={active && activeDirection === "desc" ? "primary" : "secondary"}
          icon={<ArrowDown size={12} />}
          onClick={(event) => {
            event.stopPropagation();
            onSort(field, "desc");
          }}
        />
      </span>
    </div>
  );
}

/** 最近动态气泡卡片。 */
function LatestRecordPopover({ item }: { item: ContactWorkbenchItem }) {
  return (
    <Popover
      position="left"
      trigger="hover"
      content={
        <div className="crm-contact-workbench-record-popover">
          <strong>{item.latestRecordMeetingName || "最近动态"}</strong>
          <span>类型：{item.latestRecordTypeLabel || "-"}</span>
          <span>时间：{item.latestRecordAt || "-"}</span>
          <span>内容：{item.latestRecordContent || item.latestRecordMeetingName || "暂无动态内容"}</span>
        </div>
      }
    >
      <div className="crm-contact-workbench-record-cell">
        <span>
          {item.latestRecordTypeLabel}
          {item.latestRecordAt ? ` · ${item.latestRecordAt}` : ""}
        </span>
        <strong>{item.latestRecordMeetingName || item.latestRecordContent || "暂无动态摘要"}</strong>
      </div>
    </Popover>
  );
}

/** 手机和微信气泡卡片。 */
function PhoneWechatPopover({ item }: { item: ContactWorkbenchItem }) {
  const mobileRaw = item.mobile?.trim() || "";
  const wechatRaw = item.wechat?.trim() || "";
  const hasMobile = Boolean(mobileRaw);
  const hasWechat = Boolean(wechatRaw);
  const mobile = hasMobile ? mobileRaw : "-";
  const wechat = hasWechat ? wechatRaw : "-";
  const displayLines = [
    ...(hasMobile ? [mobileRaw] : []),
    ...(hasWechat ? [wechatRaw] : []),
  ];
  if (displayLines.length === 0) {
    displayLines.push("-");
  }
  const ariaLabel = `手机/微信 ${displayLines.join(" ")}`;
  const cellInner = (
    <>
      {displayLines.map((line, index) => (
        <span key={`phone-line-${index}`}>{line}</span>
      ))}
    </>
  );
  const cell = (
    <div
      className="crm-contact-workbench-phone-cell"
      data-testid={`contact-phone-wechat-${item.id}`}
      aria-label={ariaLabel}
    >
      {item.canViewSensitiveContactInfo === false ? (
        displayLines.map((line, index) => (
          <EllipsisHover key={`phone-line-${index}`} content={line}>
            <span>{line}</span>
          </EllipsisHover>
        ))
      ) : (
        cellInner
      )}
    </div>
  );

  if (item.canViewSensitiveContactInfo === false) {
    return cell;
  }

  return (
    <Popover
      position="left"
      trigger="hover"
      content={
        <div className="crm-contact-workbench-phone-popover">
          <strong>{item.contactName}</strong>
          <span>手机：{mobile}</span>
          <span>微信：{wechat}</span>
        </div>
      }
    >
      {cell}
    </Popover>
  );
}


/** 员工下拉选择器。 */
function StaffSelect({
  value,
  options,
  placeholder,
  allowClear = false,
  dropdownMenuStyle,
  getPopupContainer,
  onChange,
}: {
  value?: number;
  options: ContactWorkbenchStaffOption[];
  placeholder: string;
  allowClear?: boolean;
  dropdownMenuStyle?: CSSProperties;
  getPopupContainer?: (node: HTMLElement) => Element;
  onChange: (value: number | undefined, displayName?: string) => void;
}) {
  const displayableOptions = options.filter(
    (option) => isDisplayableStaffOption(option) || option.id === value,
  );
  return (
    <Select
      allowClear={allowClear}
      showSearch
      placeholder={placeholder}
      value={value}
      dropdownMenuStyle={dropdownMenuStyle}
      getPopupContainer={getPopupContainer}
      filterOption={(inputValue, option) => {
        const searchText = String(option.props.searchText ?? "");
        return searchText.toLowerCase().includes(inputValue.toLowerCase());
      }}
      renderFormat={(_option, selectedValue) => {
        const selected = displayableOptions.find((option) => option.id === Number(selectedValue));
        const label = selected ? formatStaffOptionLabel(selected) : "";
        return <span title={label}>{label}</span>;
      }}
      onChange={(nextValue) => {
        if (!nextValue) {
          onChange(undefined, undefined);
          return;
        }
        const selectedId = Number(nextValue);
        const selected = displayableOptions.find((option) => option.id === selectedId);
        onChange(selectedId, selected ? formatStaffOptionLabel(selected) : undefined);
      }}
    >
      {displayableOptions.map((option) => {
        const label = formatStaffOptionLabel(option);
        return (
          <Select.Option
            key={option.id}
            value={option.id}
            disabled={option.disabled}
            title={label}
            searchText={`${label} ${option.username} ${option.phone ?? ""} ${option.id}`}
          >
            <span title={label}>{label}</span>
          </Select.Option>
        );
      })}
    </Select>
  );
}

/** 归属销售多选筛选，对齐电销任务池原负责人弹窗。 */
function ContactOwnerFilterDropdown({
  options,
  loading,
  ownerUserIds,
  sharedOwnerUserIds,
  temporaryHandlerUserIds,
  onApply,
}: {
  options?: ContactWorkbenchOwnerOptions;
  loading?: boolean;
  ownerUserIds: number[];
  sharedOwnerUserIds: number[];
  temporaryHandlerUserIds: number[];
  onApply: (next: {
    ownerUserIds: number[];
    sharedOwnerUserIds: number[];
    temporaryHandlerUserIds: number[];
  }) => void;
}) {
  const [visible, setVisible] = useState(false);
  const [tab, setTab] = useState<ContactWorkbenchOwnerKind>("primary_owner");
  const [keyword, setKeyword] = useState("");
  const [draftPrimaryIds, setDraftPrimaryIds] = useState(ownerUserIds);
  const [draftSharedIds, setDraftSharedIds] = useState(sharedOwnerUserIds);
  const [draftHandlerIds, setDraftHandlerIds] = useState(temporaryHandlerUserIds);

  useEffect(() => {
    if (!visible) {
      setDraftPrimaryIds(ownerUserIds);
      setDraftSharedIds(sharedOwnerUserIds);
      setDraftHandlerIds(temporaryHandlerUserIds);
      setKeyword("");
    }
  }, [ownerUserIds, sharedOwnerUserIds, temporaryHandlerUserIds, visible]);

  const selectedCount =
    ownerUserIds.length + sharedOwnerUserIds.length + temporaryHandlerUserIds.length;
  const currentOptions =
    tab === "shared_owner"
      ? (options?.sharedOwners ?? [])
      : tab === "temporary_handler"
        ? (options?.temporaryHandlers ?? [])
        : (options?.primaryOwners ?? []);
  const filteredOptions = filterOwnerOptionsByKeyword(currentOptions, keyword);
  const draftIds =
    tab === "shared_owner"
      ? draftSharedIds
      : tab === "temporary_handler"
        ? draftHandlerIds
        : draftPrimaryIds;
  const tabLabel =
    tab === "shared_owner" ? "共享销售" : tab === "temporary_handler" ? "临时任务" : "主销售";

  /** 勾选或取消当前页签下的销售用户。 */
  function toggleUser(userId: number, checked: boolean) {
    const update = (current: number[]) =>
      checked ? [...new Set([...current, userId])] : current.filter((id) => id !== userId);
    if (tab === "shared_owner") {
      setDraftSharedIds(update);
      return;
    }
    if (tab === "temporary_handler") {
      setDraftHandlerIds(update);
      return;
    }
    setDraftPrimaryIds(update);
  }

  return (
    <Dropdown
      trigger="click"
      position="bl"
      popupVisible={visible}
      onVisibleChange={(nextVisible) => {
        setVisible(nextVisible);
        if (!nextVisible) {
          setKeyword("");
        }
      }}
      triggerProps={{ escToClose: true }}
      droplist={(
        <div
          className="crm-contact-workbench-owner-filter-dropdown"
          role="group"
          aria-label="按归属销售筛选"
        >
          <Radio.Group
            type="button"
            size="small"
            value={tab}
            onChange={(value) => {
              setTab(value as ContactWorkbenchOwnerKind);
              setKeyword("");
            }}
            aria-label="归属销售筛选类型"
          >
            <Radio value="primary_owner">主销售</Radio>
            <Radio value="shared_owner">共享销售</Radio>
            <Radio value="temporary_handler">临时任务</Radio>
          </Radio.Group>
          <div className="crm-contact-workbench-owner-filter-title">
            选择{tabLabel}（可多选）
          </div>
          <Input
            allowClear
            size="small"
            placeholder={`模糊搜索${tabLabel}`}
            value={keyword}
            aria-label={`模糊搜索${tabLabel}`}
            onChange={setKeyword}
          />
          {loading ? (
            <div>正在加载{tabLabel}...</div>
          ) : currentOptions.length === 0 ? (
            <div>暂无可筛选的{tabLabel}</div>
          ) : filteredOptions.length === 0 ? (
            <div>未找到匹配的{tabLabel}</div>
          ) : null}
          <div className="crm-contact-workbench-owner-filter-list">
            {filteredOptions.map((option: ContactWorkbenchOwnerOption) => (
              <Checkbox
                key={`${option.ownerKind}-${option.userId}`}
                aria-label={`选择${tabLabel} ${option.userName}`}
                checked={draftIds.includes(option.userId)}
                onChange={(checked) => toggleUser(option.userId, checked)}
              >
                {option.userName}（共 {option.contactCount} 条）
              </Checkbox>
            ))}
          </div>
          <Space>
            <Button
              type="primary"
              size="small"
              disabled={draftPrimaryIds.length + draftSharedIds.length + draftHandlerIds.length === 0}
              onClick={() => {
                onApply({
                  ownerUserIds: draftPrimaryIds,
                  sharedOwnerUserIds: draftSharedIds,
                  temporaryHandlerUserIds: draftHandlerIds,
                });
                setVisible(false);
              }}
            >
              按所选{tabLabel}筛选
            </Button>
            <Button
              size="small"
              onClick={() => {
                setDraftPrimaryIds([]);
                setDraftSharedIds([]);
                setDraftHandlerIds([]);
                setKeyword("");
                onApply({
                  ownerUserIds: [],
                  sharedOwnerUserIds: [],
                  temporaryHandlerUserIds: [],
                });
                setVisible(false);
              }}
            >
              清空
            </Button>
          </Space>
        </div>
      )}
    >
      <Button
        type="text"
        size="small"
        aria-label="归属销售"
        aria-haspopup="dialog"
        aria-expanded={visible}
      >
        归属销售
        {selectedCount > 0 ? `（${selectedCount}）` : ""}
      </Button>
    </Dropdown>
  );
}

/** 把 URL 回填的归属销售名并入下拉候选，避免只显示数字 ID。 */
function mergeOwnerFilterStaffOptions(
  options: ContactWorkbenchStaffOption[],
  ownerUserId?: number,
  ownerUserName?: string,
): ContactWorkbenchStaffOption[] {
  if (!ownerUserId) {
    return options;
  }
  if (options.some((option) => option.id === ownerUserId)) {
    return options;
  }
  const displayName = ownerUserName?.trim() || "已选销售";
  return [
    {
      id: ownerUserId,
      username: "",
      displayName,
      disabled: false,
    },
    ...options,
  ];
}

/** 员工多选下拉，适用于批量追加共享销售。 */
function StaffMultiSelect({
  value,
  options,
  placeholder,
  dropdownMenuStyle,
  onChange,
}: {
  value: number[];
  options: ContactWorkbenchStaffOption[];
  placeholder: string;
  dropdownMenuStyle?: CSSProperties;
  onChange: (value: number[]) => void;
}) {
  const displayableOptions = options.filter(isDisplayableStaffOption);
  const [popupVisible, setPopupVisible] = useState(false);
  const hidePopupAfterSelect = useCallback(() => {
    window.setTimeout(() => setPopupVisible(false), 120);
  }, []);
  return (
    <Select
      allowClear
      showSearch
      mode="multiple"
      placeholder={placeholder}
      value={value}
      popupVisible={popupVisible}
      onVisibleChange={setPopupVisible}
      maxTagCount={3}
      dropdownMenuStyle={dropdownMenuStyle ?? { maxHeight: 108 }}
      filterOption={(inputValue, option) => {
        const searchText = String(option.props.searchText ?? "");
        return searchText.toLowerCase().includes(inputValue.toLowerCase());
      }}
      onChange={(nextValue) => {
        const values = Array.isArray(nextValue) ? nextValue : [];
        onChange(values.map((item) => Number(item)).filter((item) => Number.isFinite(item)));
        hidePopupAfterSelect();
      }}
      onSelect={hidePopupAfterSelect}
    >
      {displayableOptions.map((option) => {
        const label = formatStaffOptionLabel(option);
        return (
          <Select.Option
            key={option.id}
            value={option.id}
            disabled={option.disabled}
            title={label}
            searchText={`${label} ${option.username} ${option.phone ?? ""} ${option.id}`}
          >
            <span title={label}>{label}</span>
          </Select.Option>
        );
      })}
    </Select>
  );
}

/** 生成联系人投放协作任务池的默认任务名称。 */
function createDefaultTaskPoolName(
  taskType: ContactWorkbenchTaskPoolType,
  contactCount: number,
  operatorName: string,
) {
  const today = new Date();
  const month = today.getMonth() + 1;
  const day = today.getDate();
  const taskLabel = taskType === "strong_follow" ? "强跟" : "电销";
  return `${month}月${day}日${operatorName}的${contactCount}位联系人需要${taskLabel}`;
}

/** 合并跨页已选与当前页 ID，去重后保持稳定顺序。 */
export function mergeContactSelectionKeys(previousKeys: number[], currentPageIds: number[]) {
  const merged = new Set(previousKeys);
  for (const id of currentPageIds) {
    merged.add(id);
  }
  return [...merged];
}

/** 判断当前选择是否完整覆盖当前页（用于「当前页」范围按钮高亮）。 */
function isCurrentPageSelectionActive(
  selectionMode: ContactWorkbenchSelectionMode,
  selectedRowKeys: number[],
  currentPageContactIds: number[],
) {
  if (selectionMode !== "selected_rows" || currentPageContactIds.length === 0) {
    return false;
  }
  const selectedIds = new Set(selectedRowKeys);
  return currentPageContactIds.every((id) => selectedIds.has(id));
}

/** 展示当前批量动作实际作用范围。 */
function formatSelectionActionDescription(
  selectionMode: ContactWorkbenchSelectionMode,
  contactCount: number,
  actionName: "投放" | "转移" | "共享",
) {
  if (selectionMode === "all_results") {
    return `将${actionName}全部查询结果 ${contactCount} 位联系人，系统会按当前筛选条件拉取全部 ID。`;
  }
  return `将${actionName}已勾选的 ${contactCount} 位联系人。`;
}


/** 格式化销售下拉显示，乱码名称降级为账号；列表只展示姓名不附带电话。 */
export function formatStaffOptionLabel(option: ContactWorkbenchStaffOption) {
  return formatStaffOptionName(option);
}

/** 判断销售候选是否适合展示在下拉列表中。

 * 与后端 `_is_displayable_staff_option` 对齐：可读姓名、可读账号或手机号任一即可。
 * 不再要求纯中文 2～6 字，避免「陈华平11」「电销账号001」「张」等真实销售被前端二次滤掉。
 */
export function isDisplayableStaffOption(option: ContactWorkbenchStaffOption) {
  return (
    isReadableStaffDisplayName(option.displayName) ||
    isReadableStaffAccount(option.username) ||
    Boolean(option.phone?.trim())
  );
}

/** 解析销售人员展示姓名，名称异常时降级为账号。 */
function formatStaffOptionName(option: ContactWorkbenchStaffOption) {
  if (isReadableStaffDisplayName(option.displayName)) {
    return option.displayName;
  }
  if (isReadableStaffAccount(option.username)) {
    return option.username;
  }
  return option.phone?.trim() || option.username.trim() || `用户${option.id}`;
}

/** 解析当前操作人姓名，临时登录态缺失时使用首个可用销售兜底。 */
function resolveCurrentOperatorName(
  staffOptions: ContactWorkbenchStaffOption[],
  currentUserId: number,
) {
  const currentStaff = staffOptions.find(
    (option) => option.id === currentUserId && !option.disabled && isOperatorNameUsable(option),
  );
  return currentStaff ? formatStaffOptionName(currentStaff) : "当前操作人";
}

/** 粗略识别常见编码污染字符。 */
function looksLikeMojibake(value: string) {
  const normalized = value.trim();
  if (!normalized) {
    return false;
  }
  if (/[锘�\uFFFDÃÂâ€æåçèé]/.test(normalized)) {
    return true;
  }
  const commonGbkMojibakeFragments =
    /(鐜|嬭|幑|闄|堝|鏉|庢|槑|鍒|樺|寮|犺|佸|笀|娴|欐|睙|鍏|朵|粬|攢|鍞|瀛|櫌|绯|粺|鈥|銆|锛)/;
  return commonGbkMojibakeFragments.test(normalized);
}

/** 判断销售姓名/账号是否适合作为下拉主展示文本。 */
function isReadableStaffDisplayName(value: string) {
  const normalized = value.trim();
  if (!normalized || looksLikeMojibake(normalized)) {
    return false;
  }
  if (/[?？]/u.test(normalized) || /^[^\p{Script=Han}A-Za-z0-9]/u.test(normalized)) {
    return false;
  }
  return /[\p{Script=Han}A-Za-z]/u.test(normalized);
}

/** 判断账号是否可作为姓名缺失时的兜底展示。 */
function isReadableStaffAccount(value: string) {
  const normalized = value.trim();
  if (!normalized || looksLikeMojibake(normalized)) {
    return false;
  }
  if (normalized.includes("@") || /^\d{7,}$/.test(normalized)) {
    return false;
  }
  return /^[A-Za-z][A-Za-z0-9_.-]{2,}$/.test(normalized);
}

/** 判断候选是否适合用于默认任务名中的操作人名称。 */
function isOperatorNameUsable(option: ContactWorkbenchStaffOption) {
  return isReadableChineseStaffName(option.displayName);
}

/** 判断候选是否具备可展示的中文员工姓名。 */
function isReadableChineseStaffName(value: string) {
  return isReadableStaffDisplayName(value) && /^[\p{Script=Han}]{2,6}$/u.test(value.trim());
}

/** 把联系人星级筛选状态映射为选择控件值。 */
function resolveContactStarSelectValue(contactStar?: string, contactStarUnset?: boolean) {
  if (contactStarUnset) {
    return "__unset__";
  }
  const normalized = (contactStar ?? "").trim();
  return normalized || undefined;
}

/** 筛选星级选项：优先用元数据，缺省回退固定枚举，避免下拉空列表。 */
function resolveContactStarFilterOptions(options?: string[]) {
  if (options && options.length > 0) {
    return options;
  }
  return Object.keys(CONTACT_STAR_OPTION_DESCRIPTIONS);
}

/** 把星级选择控件值映射为互斥的服务端筛选参数。 */
function parseContactStarSelectValue(value?: string) {
  if (value === "__unset__") {
    return { contactStar: undefined, contactStarUnset: true };
  }
  return { contactStar: value || undefined, contactStarUnset: false };
}

/** 把布尔状态映射为下拉值。 */
function resolveStatusSelectValue(value?: boolean) {
  if (value === undefined) {
    return undefined;
  }
  return value ? "true" : "false";
}

/** 把联系人状态下拉值映射为布尔筛选值。 */
function parseStatusSelectValue(value?: string) {
  if (value === "true") {
    return true;
  }
  if (value === "false") {
    return false;
  }
  return undefined;
}


